
import React, { useState, useEffect, useMemo, useRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { showToast } from "../utils/customToast";
import axios from "axios";
import "./Invoice.css";
import { SECURITY_KEY } from "../globals";
import { useNavigate, useLocation, useSearchParams, useParams } from "react-router-dom";
import { apiWithLog } from "../utils/apiWithLog";
import { formatInvoiceNumber, extractInvoiceNumeric, getFinancialYear } from "../utils/financialYearHelper";
import { SkeletonBone } from "../Component/Skeleton";
import { cleanPartyOrAddress } from "../utils/stringUtils";

const TRANSPORT_WITH_DUTY_SLIP = "Transport with Duty Slip";
const TRANSPORT_WITHOUT_DUTY_SLIP = "Transport without Duty Slip";

// ----- helper: build breakdown string (matches backend format) -----
const parseTimeToMins = (timeStr) => {
  if (!timeStr) return 0;
  if (typeof timeStr === "number") return timeStr * 60;
  const str = String(timeStr).trim();
  const match = str.match(/(\d+(?:\.\d+)?)\s*h(?:rs?)?\s*(\d*(?:\.\d+)?)?\s*m?/i);
  if (match) {
    const hrs = Number(match[1]) || 0;
    const mins = Number(match[2]) || 0;
    return hrs * 60 + mins;
  }
  const num = Number(str);
  return !isNaN(num) ? num * 60 : 0;
};

export const checkIsOutstation = (s, farePackagesList = []) => {
  if (!s) return false;
  const pType = s.packageType ?? s.PackageType ?? s.DayPackageType ?? s.dayPackageType;
  if (pType !== undefined && pType !== null) {
    if (Number(pType) === 1 || String(pType) === "1") return true;
    if (Number(pType) === 0 || String(pType) === "0") return false;
  }
  const pkgName = (s.PackageName || s.packageName || s.pkgName || s.package || "").trim().toLowerCase();
  if (Array.isArray(farePackagesList) && farePackagesList.length > 0 && pkgName) {
    const matched = farePackagesList.find(
      (p) => (p.PackageName || p.packageName || "").trim().toLowerCase() === pkgName
    );
    if (matched) {
      const mType = matched.PackageType ?? matched.packageType;
      if (mType !== undefined && mType !== null) {
        return Number(mType) === 1 || String(mType) === "1";
      }
    }
  }
  if (Array.isArray(s.dateWiseDetails) && s.dateWiseDetails.length > 0) {
    const hasOutstationDay = s.dateWiseDetails.some(
      (d) => Number(d.packageType ?? d.PackageType ?? d.DayPackageType) === 1
    );
    if (hasOutstationDay) return true;
  }
  return /outstation/i.test(pkgName);
};

export const parsePackageIncludedKM = (pkgName, explicitKM) => {
  if (explicitKM !== undefined && explicitKM !== null && explicitKM !== "" && Number(explicitKM) > 0) {
    return Number(explicitKM);
  }
  if (!pkgName) return 0;
  const match = String(pkgName).match(/(\d+(?:\.\d+)?)\s*KM\b/i);
  return match ? Number(match[1]) : 0;
};

export const parsePackageIncludedHrs = (pkgName, explicitHrs) => {
  if (explicitHrs !== undefined && explicitHrs !== null && explicitHrs !== "" && Number(explicitHrs) > 0) {
    return Number(explicitHrs);
  }
  if (!pkgName) return 0;
  const match = String(pkgName).match(/(\d+(?:\.\d+)?)\s*H(?:RS?)?\b/i);
  return match ? Number(match[1]) : 0;
};

export const resolveUpgradedPackage = (currentFare, actualKM, actualHours, availablePackages = []) => {
  if (!currentFare || !Array.isArray(availablePackages) || availablePackages.length === 0) {
    return { pkg: currentFare || {}, wasUpgraded: false };
  }

  const baseIncKM = parsePackageIncludedKM(currentFare.PackageName || currentFare.packageName, currentFare.IncludedKM || currentFare.includedKM || currentFare.incKM);
  const baseIncHrs = parsePackageIncludedHrs(currentFare.PackageName || currentFare.packageName, currentFare.IncludedHrs || currentFare.includedHrs || currentFare.incHrs);
  const basePkgType = Number(currentFare.PackageType ?? currentFare.packageType ?? 0);
  const numActualKM = Number(actualKM) || 0;

  // Filter packages of the same type that are higher than base package
  const higherCandidates = availablePackages.filter((p) => {
    const pType = Number(p.PackageType ?? p.packageType ?? 0);
    if (pType !== basePkgType) return false;
    const pKM = parsePackageIncludedKM(p.PackageName || p.packageName, p.IncludedKM || p.includedKM);
    const pHrs = parsePackageIncludedHrs(p.PackageName || p.packageName, p.IncludedHrs || p.includedHrs);
    return pKM > baseIncKM || (pKM === baseIncKM && pHrs > baseIncHrs);
  });

  if (!higherCandidates.length) {
    return { pkg: currentFare, wasUpgraded: false };
  }

  // Filter candidates where actual KM reached or exceeded the candidate's included KM
  const qualifying = higherCandidates.filter((p) => {
    const pKM = parsePackageIncludedKM(p.PackageName || p.packageName, p.IncludedKM || p.includedKM);
    return pKM > 0 && numActualKM >= pKM;
  });

  if (!qualifying.length) {
    return { pkg: currentFare, wasUpgraded: false };
  }

  // Pick the highest qualifying package (max KM, then max Hrs)
  qualifying.sort((a, b) => {
    const aKM = parsePackageIncludedKM(a.PackageName || a.packageName, a.IncludedKM || a.includedKM);
    const bKM = parsePackageIncludedKM(b.PackageName || b.packageName, b.IncludedKM || b.includedKM);
    if (bKM !== aKM) return bKM - aKM;
    const aHrs = parsePackageIncludedHrs(a.PackageName || a.packageName, a.IncludedHrs || a.includedHrs);
    const bHrs = parsePackageIncludedHrs(b.PackageName || b.packageName, b.IncludedHrs || b.includedHrs);
    return bHrs - aHrs;
  });

  const best = qualifying[0];
  const upgradedIncKM = parsePackageIncludedKM(best.PackageName || best.packageName, best.IncludedKM || best.includedKM);
  const upgradedIncHrs = parsePackageIncludedHrs(best.PackageName || best.packageName, best.IncludedHrs || best.includedHrs);

  return {
    pkg: {
      ...currentFare,
      FareId: best.ID || best.PackageID || currentFare.FareId,
      PackageID: best.PackageID || best.ID || currentFare.PackageID,
      PackageName: best.PackageName || best.packageName || currentFare.PackageName,
      PackageType: best.PackageType !== undefined ? Number(best.PackageType) : basePkgType,
      BaseFare: Number(best.BaseFare ?? best.baseFare) || 0,
      FuelRateKM: Number(best.FuelRateKM ?? best.fuelRateKM) || 0,
      LubRateKM: Number(best.LubRateKM ?? best.lubRateKM) || 0,
      TimeRatePM: Number(best.TimeRatePM ?? best.timeRatePM) || 0,
      IncludedKM: upgradedIncKM,
      IncludedHrs: upgradedIncHrs,
      isPackageUpgraded: true,
      originalPackageName: currentFare.PackageName || currentFare.packageName,
      originalIncludedKM: baseIncKM,
      originalIncludedHrs: baseIncHrs,
      originalBaseFare: Number(currentFare.BaseFare ?? currentFare.baseFare) || 0,
    },
    wasUpgraded: true,
    originalPackage: currentFare,
  };
};

export const calculateDutySlipDayMetrics = (d, fallbackSlip = {}, farePackagesList = []) => {
  if (!d) return null;
  let pkgName = (d.packageName || d.DayPackageName || d.PackageName || fallbackSlip?.packageName || fallbackSlip?.PackageName || fallbackSlip?.DayPackageName || "").trim();
  const fallbackKmRate = Number(fallbackSlip?.FuelRateKM || fallbackSlip?.fuelRateKM || fallbackSlip?.kmRate || fallbackSlip?.KmRate || 0);
  const fallbackLubRate = Number(fallbackSlip?.LubRateKM || fallbackSlip?.lubRateKM || 0);
  const fallbackTimeRate = Number(fallbackSlip?.TimeRatePM || fallbackSlip?.timeRatePM || fallbackSlip?.TimeRate || fallbackSlip?.timeRate || 0);
  let fallbackIncKm = Number(fallbackSlip?.IncludedKM || fallbackSlip?.includedKM || fallbackSlip?.incKM || 0);
  let fallbackIncHrs = Number(fallbackSlip?.IncludedHrs || fallbackSlip?.includedHrs || fallbackSlip?.incHrs || 0);
  const fallbackBaseFare = Number(fallbackSlip?.BaseFare || fallbackSlip?.baseFare || 0);

  if (pkgName) {
    if (!fallbackIncHrs) {
      const hrMatch = pkgName.match(/(\d+(?:\.\d+)?)\s*H(?:RS?)?\b/i);
      if (hrMatch) fallbackIncHrs = Number(hrMatch[1]);
    }
    if (!fallbackIncKm) {
      const kmMatch = pkgName.match(/(\d+(?:\.\d+)?)\s*KM\b/i);
      if (kmMatch) fallbackIncKm = Number(kmMatch[1]);
    }
  }

  let baseFare = d.baseFare !== undefined && d.baseFare !== null && d.baseFare !== "" ? Number(d.baseFare) : fallbackBaseFare;
  let incKM = d.includedKM !== undefined && d.includedKM !== null && d.includedKM !== "" ? Number(d.includedKM) : fallbackIncKm;
  let incHrs = d.includedHrs !== undefined && d.includedHrs !== null && d.includedHrs !== "" ? Number(d.includedHrs) : fallbackIncHrs;
  let fuelRate = d.fuelRateKM !== undefined && d.fuelRateKM !== null && d.fuelRateKM !== "" ? Number(d.fuelRateKM) : fallbackKmRate;
  let lubRate = d.lubRateKM !== undefined && d.lubRateKM !== null && d.lubRateKM !== "" ? Number(d.lubRateKM) : fallbackLubRate;
  let timeRate = d.timeRatePM !== undefined && d.timeRatePM !== null && d.timeRatePM !== "" ? Number(d.timeRatePM) : fallbackTimeRate;

  // Check client-side package upgrade if packages are available
  const availablePkgs = (Array.isArray(farePackagesList) && farePackagesList.length > 0)
    ? farePackagesList
    : (fallbackSlip?.availableReceivablePackages || fallbackSlip?.availablePayablePackages || []);
  const actualDayKm = Number(d.totalKm ?? d.km ?? fallbackSlip?.TotalKM ?? 0);
  const actualDayTime = d.totalTime || fallbackSlip?.TotalTime;
  const actualDayTimeHours = parseTimeToMins(actualDayTime) / 60;

  if (availablePkgs.length > 0 && actualDayKm > 0) {
    const rawDayFare = {
      PackageName: pkgName,
      PackageType: d.packageType ?? fallbackSlip?.packageType ?? fallbackSlip?.PackageType ?? 0,
      BaseFare: baseFare,
      IncludedKM: incKM,
      IncludedHrs: incHrs,
      FuelRateKM: fuelRate,
      LubRateKM: lubRate,
      TimeRatePM: timeRate,
    };
    const upgraded = resolveUpgradedPackage(rawDayFare, actualDayKm, actualDayTimeHours, availablePkgs);
    if (upgraded.wasUpgraded) {
      pkgName = upgraded.pkg.PackageName;
      baseFare = upgraded.pkg.BaseFare;
      incKM = upgraded.pkg.IncludedKM;
      incHrs = upgraded.pkg.IncludedHrs;
      if (upgraded.pkg.FuelRateKM) fuelRate = upgraded.pkg.FuelRateKM;
      if (upgraded.pkg.TimeRatePM) timeRate = upgraded.pkg.TimeRatePM;
    }
  }

  if (fuelRate === null || (fuelRate === 0 && fallbackKmRate > 0)) {
    fuelRate = fallbackKmRate;
  }
  if (lubRate === null || (lubRate === 0 && fallbackLubRate > 0)) {
    lubRate = fallbackLubRate;
  }
  if (timeRate === null || (timeRate === 0 && fallbackTimeRate > 0)) {
    timeRate = fallbackTimeRate;
  }

  const kmRate = fuelRate + lubRate;

  // Detect Outstation by PackageType (0 = Local, 1 = Outstation), or fallback to name / route / daily commitment
  const isOutstation = checkIsOutstation(d, farePackagesList) || checkIsOutstation(fallbackSlip, farePackagesList);

  // Compute baseFare for Outstation or whenever BaseFare is 0 with valid IncludedKM and Per-KM rate
  if (isOutstation || (baseFare === 0 && incKM > 0 && (fuelRate > 0 || kmRate > 0))) {
    const effectiveKm = incKM > 0 ? incKM : 250;
    const effectiveRate = fuelRate > 0 ? fuelRate : (kmRate > 0 ? kmRate : 0);
    if (effectiveRate > 0 && (baseFare === 0 || isOutstation)) {
      baseFare = effectiveKm * effectiveRate;
    }
    if (!incKM) incKM = effectiveKm;
  }

  return {
    pkgName,
    packageType: isOutstation ? 1 : 0,
    baseFare: Number(baseFare) || 0,
    incKM: Number(incKM) || 0,
    incHrs: Number(incHrs) || 0,
    kmRate: fuelRate > 0 ? fuelRate : (kmRate > 0 ? kmRate : 0),
    fuelRate: Number(fuelRate) || 0,
    lubRate: Number(lubRate) || 0,
    timeRate: Number(timeRate) || 0,
  };
};

const resolveDayPackageMetrics = (d, fallbackSlip, farePackagesList = []) => {
  return calculateDutySlipDayMetrics(d, fallbackSlip, farePackagesList);
};

const calculateSlipTotal = (s, farePackagesList = []) => {
  if (!s) return 0;
  const details = Array.isArray(s.dateWiseDetails) ? s.dateWiseDetails : [];
  const parking = Number(s.Parking || s.parking) || 0;
  const driverAllowance = Number(s.DriverAllowance || s.driverAllowance) || 0;
  const tollTax = Number(s.TollTax || s.tollTax) || 0;
  const nightCharges = Number(s.NightCharges || s.nightCharges) || 0;
  const extras = parking + driverAllowance + tollTax + nightCharges;

  if (details.length > 0) {
    let totalBaseFare = 0;
    let totalIncKM = 0;
    let totalIncMins = 0;
    let totalActualKM = 0;
    let totalActualMins = 0;
    let mainKmRate = 0;
    let mainTimeRate = 0;

    details.forEach((d) => {
      const m = calculateDutySlipDayMetrics(d, s, farePackagesList);
      const dayKm = Number(d.totalKm) || (details.length === 1 ? Number(s.TotalKM) || 0 : 0);
      const dayMins = parseTimeToMins(d.totalTime) || (details.length === 1 ? parseTimeToMins(s.TotalTime) : 0);

      totalBaseFare += m.baseFare;
      totalIncKM += m.incKM;
      totalIncMins += m.incHrs * 60;
      totalActualKM += dayKm;
      totalActualMins += dayMins;

      if (m.kmRate > 0) mainKmRate = m.kmRate;
      if (m.timeRate > 0) mainTimeRate = m.timeRate;
    });

    if (mainKmRate === 0) {
      mainKmRate = Number(s.FuelRateKM) || 0;
    }
    if (mainTimeRate === 0) {
      mainTimeRate = Number(s.TimeRatePM) || 0;
    }

    const extraKM = Math.max(0, totalActualKM - totalIncKM);
    const extraMins = Math.max(0, totalActualMins - totalIncMins);

    const kmCharge = extraKM * mainKmRate;
    const timeCharge = (extraMins / 60) * mainTimeRate;

    return Math.round(totalBaseFare + kmCharge + timeCharge + extras);
  }

  const availablePkgs = (Array.isArray(farePackagesList) && farePackagesList.length > 0)
    ? farePackagesList
    : (s?.availableReceivablePackages || s?.availablePayablePackages || []);
  const totalKm = Number(s.TotalKM || s.totalKM || s.totalKm || 0);
  const totalMins = parseTimeToMins(s.TotalTime);

  let activeBaseFare = Number(s.BaseFare || s.baseFare || 0);
  let activeIncKM = Number(s.IncludedKM || s.includedKM || 0);
  let activeIncHrs = Number(s.IncludedHrs || s.includedHrs || 0);
  let activeFuelRate = Number(s.FuelRateKM || s.fuelRateKM || 0);
  let activeLubRate = Number(s.LubRateKM || s.lubRateKM || 0);
  let activeTimeRate = Number(s.TimeRatePM || s.timeRatePM || 0);

  if (availablePkgs.length > 0 && totalKm > 0) {
    const rawFare = {
      PackageName: s.PackageName || s.packageName,
      PackageType: s.PackageType ?? s.packageType ?? 0,
      BaseFare: activeBaseFare,
      IncludedKM: activeIncKM,
      IncludedHrs: activeIncHrs,
      FuelRateKM: activeFuelRate,
      LubRateKM: activeLubRate,
      TimeRatePM: activeTimeRate,
    };
    const up = resolveUpgradedPackage(rawFare, totalKm, totalMins / 60, availablePkgs);
    if (up.wasUpgraded) {
      activeBaseFare = up.pkg.BaseFare;
      activeIncKM = up.pkg.IncludedKM;
      activeIncHrs = up.pkg.IncludedHrs;
      if (up.pkg.FuelRateKM) activeFuelRate = up.pkg.FuelRateKM;
      if (up.pkg.TimeRatePM) activeTimeRate = up.pkg.TimeRatePM;
    }
  }

  const isOutstation = checkIsOutstation(s, farePackagesList);
  const kmRate = activeFuelRate + activeLubRate;
  const timeRate = activeTimeRate;
  const incKM = activeIncKM;
  const incMins = activeIncHrs * 60;

  let baseFare = activeBaseFare;
  if (isOutstation || (baseFare === 0 && incKM > 0 && kmRate > 0)) {
    const effKm = incKM > 0 ? incKM : 250;
    baseFare = effKm * kmRate;
  }

  const extraKM = Math.max(0, totalKm - incKM);
  const extraMins = Math.max(0, totalMins - incMins);

  const kmCharge = extraKM * kmRate;
  const timeCharge = (extraMins / 60) * timeRate;

  return Math.round(baseFare + kmCharge + timeCharge + extras);
};

const getBreakdownString = (slip) => {
  const parking = Number(slip.Parking || slip.parking) || 0;
  const driverAllowance = Number(slip.DriverAllowance || slip.driverAllowance) || 0;
  const tollTax = Number(slip.TollTax || slip.tollTax) || 0;
  const nightCharges = Number(slip.NightCharges || slip.nightCharges) || 0;
  const extras = parking + driverAllowance + tollTax + nightCharges;

  const details = Array.isArray(slip.dateWiseDetails) ? slip.dateWiseDetails : [];

  // Cumulative multi-day calculation if details are available
  if (details.length > 0) {
    let totalBaseFare = 0;
    let totalIncKM = 0;
    let totalIncMins = 0;
    let totalActualKM = 0;
    let totalActualMins = 0;
    let mainKmRate = 0;
    let mainTimeRate = 0;

    const daySummaries = details.map((d, i) => {
      const m = resolveDayPackageMetrics(d, slip);
      const dayKm = Number(d.totalKm) || (details.length === 1 ? Number(slip.TotalKM) || 0 : 0);
      const dayMins = parseTimeToMins(d.totalTime) || (details.length === 1 ? parseTimeToMins(slip.TotalTime) : 0);

      totalBaseFare += m.baseFare;
      totalIncKM += m.incKM;
      totalIncMins += m.incHrs * 60;
      totalActualKM += dayKm;
      totalActualMins += dayMins;

      if (m.kmRate > 0) mainKmRate = m.kmRate;
      if (m.timeRate > 0) mainTimeRate = m.timeRate;

      return `Day ${i + 1} [${m.pkgName || "Base"}]: Base ₹${m.baseFare} (${dayKm}km)`;
    });

    if (mainKmRate === 0) mainKmRate = Number(slip.FuelRateKM) || 0;
    if (mainTimeRate === 0) mainTimeRate = Number(slip.TimeRatePM) || 0;

    const extraKM = Math.max(0, totalActualKM - totalIncKM);
    const extraMins = Math.max(0, totalActualMins - totalIncMins);

    const kmCharge = extraKM * mainKmRate;
    const timeCharge = (extraMins / 60) * mainTimeRate;

    const addOnList = [];
    if (extraKM > 0) addOnList.push(`${extraKM}km extra × ₹${mainKmRate}/km`);
    if (extraMins > 0) addOnList.push(`${(extraMins / 60).toFixed(1)}h extra × ₹${mainTimeRate}/h`);
    if (parking > 0) addOnList.push(`Parking = ₹${parking}`);
    if (driverAllowance > 0) addOnList.push(`DA = ₹${driverAllowance}`);
    if (tollTax > 0) addOnList.push(`Toll = ₹${tollTax}`);
    if (nightCharges > 0) addOnList.push(`Night = ₹${nightCharges}`);

    const extraStr = addOnList.length ? ` | Add-ons: ${addOnList.join(" + ")}` : "";
    const grandTotal = Math.round(totalBaseFare + kmCharge + timeCharge + extras);
    return `${totalActualKM || slip.TotalKM}km, ${slip.TotalTime}hrs — ${daySummaries.join(" | ")}${extraStr} = ₹${grandTotal.toLocaleString("en-IN")}`;
  }

  // Single calculation fallback
  const totalMinutes = (Number(slip.TotalTime) || 0) * 60;
  const includedMinutes = (Number(slip.IncludedHrs) || 0) * 60;
  const extraKM = Math.max(0, (Number(slip.TotalKM) || 0) - (Number(slip.IncludedKM) || 0));
  const extraMinutes = Math.max(0, totalMinutes - includedMinutes);
  const kmRate = Number(slip.FuelRateKM) || 0;
  const kmCharge = extraKM * kmRate;
  const timeCharge = (extraMinutes / 60) * Number(slip.TimeRatePM || 0);
  const baseFare = Number(slip.BaseFare) || 0;

  const parts = [`Base ₹${baseFare.toFixed(0)}`];
  const addOnParts = [];

  if (extraKM > 0) addOnParts.push(`${extraKM}km extra × ₹${kmRate.toFixed(2)}/km = ₹${Math.round(kmCharge).toLocaleString("en-IN")}`);
  if (extraMinutes > 0) addOnParts.push(`${(extraMinutes / 60).toFixed(2)}hrs extra × ₹${slip.TimeRatePM}/hr = ₹${Math.round(timeCharge).toLocaleString("en-IN")}`);
  if (parking > 0) addOnParts.push(`Parking = ₹${Math.round(parking).toLocaleString("en-IN")}`);
  if (driverAllowance > 0) addOnParts.push(`Driver Allowance = ₹${Math.round(driverAllowance).toLocaleString("en-IN")}`);
  if (tollTax > 0) addOnParts.push(`Toll Tax = ₹${Math.round(tollTax).toLocaleString("en-IN")}`);
  if (nightCharges > 0) addOnParts.push(`Night Charges = ₹${Math.round(nightCharges).toLocaleString("en-IN")}`);

  if (addOnParts.length) parts.push(`Add-on: ${addOnParts.join(" + ")}`);

  const total = baseFare + kmCharge + timeCharge + extras;
  return `${slip.TotalKM}km, ${slip.TotalTime}hrs — ${parts.join(" + ")} = ₹${Math.round(total).toLocaleString("en-IN")}`;
};

// ----- trip route string (from date-wise legs) -----
const getTripDetailsString = (slip) => {
  if (!slip) return "";

  // 1. Date-wise details (most specific)
  if (Array.isArray(slip.dateWiseDetails) && slip.dateWiseDetails.length) {
    const legs = slip.dateWiseDetails
      .map((d) => (d.tripDetails || d.TripDetails || "").trim())
      .filter(Boolean);
    if (legs.length) return legs.join(", ");
  }

  // 2. Direct route fields
  const direct = (slip.TripDetails || slip.tripDetails || slip.Route || slip.route || "").trim();
  if (direct) return direct;

  // 3. PackageName that contains "to" (e.g. "Jaipur to Pushkar")
  const pkg = (slip.PackageName || slip.packageName || "").trim();
  if (/\bto\b/i.test(pkg)) return pkg;

  return "";
};


// ----- date range display: single date if same, else "DD-MM-YYYY to DD-MM-YYYY" -----
const formatDateRangeDisplay = (openDate, closeDate) => {
  const open = toDisplayDate(toInputDate(openDate));
  const close = toDisplayDate(toInputDate(closeDate));
  if (!open) return "";
  if (!close || open === close) return open;
  return `${open} to ${close}`;
};

const isDutySlipService = (svcName) => {
  if (!svcName) return false;
  const s = svcName.trim().toLowerCase();
  return (
    s === TRANSPORT_WITH_DUTY_SLIP.toLowerCase() ||
    s === TRANSPORT_WITHOUT_DUTY_SLIP.toLowerCase() ||
    s.includes("duty slip")
  );
};
// ----- blank invoice item (manual) -----
const blankInvoiceItem = () => ({
  date: new Date().toISOString().split("T")[0],
  openingDate: new Date().toISOString().split("T")[0],
  closingDate: new Date().toISOString().split("T")[0],
  service: "",
  Particulars: "",
  quantity: 1,
  unit: "",
  rate: "",
  amount: 0,
  HSNCODE: "",
  TaxRate: 0,
  TaxAmount: 0,
  TaxName: "",
  TaxID: null,
  isAutoGenerated: false,
  linkedDutySlipIds: [],
  linkedPackageId: null,
});

// ----- date formatting -----
const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("T")[0].split(" ")[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().split("T")[0];
};

const toDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const cleanDate = isoDate.split("T")[0].split(" ")[0];
  const parts = cleanDate.split("-");
  if (parts.length !== 3) return isoDate;
  const [y, m, d] = parts;
  return `${d}-${m}-${y}`;
};

const DEFAULT_GST_STATES = [
  { ID: 34, State: "Andaman & Nicobar Islands", Code: "34" },
  { ID: 27, State: "Andhra Pradesh (Before bifurcation)", Code: "27" },
  { ID: 36, State: "Andhra Pradesh (New)", Code: "36" },
  { ID: 12, State: "Arunachal Pradesh", Code: "12" },
  { ID: 18, State: "Assam", Code: "18" },
  { ID: 10, State: "Bihar", Code: "10" },
  { ID: 4, State: "Chandigarh", Code: "04" },
  { ID: 22, State: "Chhattisgarh", Code: "22" },
  { ID: 25, State: "Daman and Diu, Dadra and Nagar Haveli", Code: "25" },
  { ID: 7, State: "Delhi", Code: "07" },
  { ID: 29, State: "Goa", Code: "29" },
  { ID: 24, State: "Gujarat", Code: "24" },
  { ID: 6, State: "Haryana", Code: "06" },
  { ID: 2, State: "Himachal Pradesh", Code: "02" },
  { ID: 1, State: "Jammu & Kashmir", Code: "01" },
  { ID: 20, State: "Jharkhand", Code: "20" },
  { ID: 28, State: "Karnataka", Code: "28" },
  { ID: 31, State: "Kerala", Code: "31" },
  { ID: 37, State: "Ladakh", Code: "37" },
  { ID: 30, State: "Lakshadweep", Code: "30" },
  { ID: 23, State: "Madhya Pradesh", Code: "23" },
  { ID: 26, State: "Maharashtra", Code: "26" },
  { ID: 14, State: "Manipur", Code: "14" },
  { ID: 17, State: "Meghalaya", Code: "17" },
  { ID: 15, State: "Mizoram", Code: "15" },
  { ID: 13, State: "Nagaland", Code: "13" },
  { ID: 21, State: "Odisha", Code: "21" },
  { ID: 33, State: "Puducherry", Code: "33" },
  { ID: 3, State: "Punjab", Code: "03" },
  { ID: 8, State: "Rajasthan", Code: "08" },
  { ID: 11, State: "Sikkim", Code: "11" },
  { ID: 32, State: "Tamil Nadu", Code: "32" },
  { ID: 35, State: "Telangana", Code: "35" },
  { ID: 16, State: "Tripura", Code: "16" },
  { ID: 9, State: "Uttar Pradesh", Code: "09" },
  { ID: 5, State: "Uttarakhand", Code: "05" },
  { ID: 19, State: "West Bengal", Code: "19" }
];

const customSelectStyles = {
  container: (base) => ({
    ...base,
    flex: 1,
    width: "100%",
  }),
  control: (base, state) => ({
    ...base,
    minHeight: "28px",
    height: "28px",
    fontSize: "11.5px",
    borderColor: state.isFocused ? "var(--accent-teal, #008b8b)" : "var(--border-input, #cbd5e1)",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 139, 139, 0.15)" : "none",
    borderRadius: "4px",
    padding: "0",
    cursor: "text",
    "&:hover": {
      borderColor: "var(--accent-teal, #008b8b)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    height: "28px",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: "0",
    padding: "0",
    fontSize: "11.5px",
    color: "var(--text-dark, #1e293b)",
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: "11.5px",
    color: "#94a3b8",
    margin: "0",
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "11.5px",
    color: "var(--text-dark, #1e293b)",
    margin: "0",
    fontWeight: 500,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "28px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "1px 4px",
    color: "#64748b",
    svg: {
      width: "13px",
      height: "13px",
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "1px 4px",
    color: "#94a3b8",
    cursor: "pointer",
    svg: {
      width: "13px",
      height: "13px",
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999999,
    fontSize: "12px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
    borderRadius: "6px",
    border: "1px solid #e2e8f0",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "220px",
    padding: "4px",
  }),
  option: (base, state) => ({
    ...base,
    padding: "5px 8px",
    fontSize: "11.5px",
    borderRadius: "3px",
    backgroundColor: state.isSelected
      ? "var(--accent-teal, #008b8b)"
      : state.isFocused
      ? "rgba(0, 139, 139, 0.08)"
      : "transparent",
    color: state.isSelected ? "#ffffff" : "#1e293b",
    cursor: "pointer",
  }),
};

const BillsInvoice = () => {
  const [parties, setParties] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [services, setServices] = useState([]);
  const [activeServiceRow, setActiveServiceRow] = useState(null);
  const [manualInvoice, setManualInvoice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // GST States and Party Duty Slip Filter states
  const [gstStates, setGstStates] = useState(DEFAULT_GST_STATES);
  const [partyFilter, setPartyFilter] = useState("all"); // 'all' | 'with_duty_slip'
  const [allCompanyDutySlips, setAllCompanyDutySlips] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const params = useParams();

  const editParam =
    searchParams.get("edit") ||
    searchParams.get("invoiceNo") ||
    params.invoiceNo ||
    params["*"] ||
    location.state?.editInvoiceNo ||
    location.state?.invoiceNo ||
    "";

  const isModifyMode =
    Boolean(editParam) ||
    searchParams.get("mode") === "modify" ||
    location.pathname.toLowerCase().includes("modify");

  const dutySlipIdParam = searchParams.get("dutySlipId") || location.state?.dutySlipId;
  const preselectedSlipHandled = useRef(false);

  const [editingInvoiceNo, setEditingInvoiceNo] = useState(editParam || "");
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);
  const [loadedInvoiceId, setLoadedInvoiceId] = useState(null);

  const [showAllOnFocusIndex, setShowAllOnFocusIndex] = useState(null);
  const [confirmedDutySlipRows, setConfirmedDutySlipRows] = useState({});
  const [particularsDropdownOpen, setParticularsDropdownOpen] = useState(null);

  // ── Transport data state ──
  const [uninvoicedDutySlips, setUninvoicedDutySlips] = useState([]);
  const [farePackages, setFarePackages] = useState([]);
  const [editingSlipId, setEditingSlipId] = useState(null);
  const [slipEditForm, setSlipEditForm] = useState({});

  // ── Form data ──
  const [formData, setFormData] = useState({
    invoiceNo: "",
    dutySlipNo: "",
    invoiceDate: "",
    vehicleNo: "",
    vehicleType: "",
    driverName: "",
    reverseCharge: "No",
    partyName: "",
    guest: "",
    bookedBy: "",
    address: "",
    state: "",
    stateCode: "",
    gstin: "",
    lessAdvance: "",
  });

  const [companyConfig, setCompanyConfig] = useState(null);
  const [incomeLedgers, setIncomeLedgers] = useState([]);
  const [expenseLedgers, setExpenseLedgers] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configIncomeSelect, setConfigIncomeSelect] = useState("");
  const [configIncomeNew, setConfigIncomeNew] = useState("");
  const [configExpenseSelect, setConfigExpenseSelect] = useState("");
  const [configExpenseNew, setConfigExpenseNew] = useState("");
  const [savingConfigModal, setSavingConfigModal] = useState(false);

  const [items, setItems] = useState([blankInvoiceItem()]);

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  // ---- Watch company change ----
  useEffect(() => {
    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    setSelectedCompanyId(company?.id || null);
  }, [localStorage.getItem("selectedCompany")]);

  // ---- Fetch dropdowns on company change ----
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    if (!isModifyMode) {
      setFormData((prev) => ({ ...prev, invoiceDate: today }));
    }

    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    const CompID = company?.id;
    if (!CompID) return;

    axios
      .get(`${BASE_API_URL}/invoice/dropdowns/${CompID}`)
      .then((res) => {
        if (res.data.success) {
          const list = (res.data.data || []).map((p) => ({
            ...p,
            HEADName: cleanPartyOrAddress(p.HEADName),
            ADDRESS1: cleanPartyOrAddress(p.ADDRESS1),
          }));
          setParties(list);
        }
      })
      .catch((err) => console.error("Error fetching parties:", err));

    axios
      .get(`${BASE_API_URL}/invoice/vehicles/${CompID}`)
      .then((res) => {
        if (res.data.success) setVehicles(res.data.data);
      })
      .catch((err) => console.error("Error fetching vehicles:", err));

    axios
      .get(`${BASE_API_URL}/invoice/services/${CompID}`)
      .then((res) => {
        if (res.data.success) setServices(res.data.data);
      })
      .catch((err) => console.error("Error fetching services:", err))
      .finally(() => setLoading(false));

    // Fetch company configuration & ledgers
    axios
      .get(`${BASE_API_URL}/configuration/${CompID}`)
      .then((res) => {
        if (res.data?.success) {
          const cfg = res.data.data;
          setCompanyConfig(cfg);
          const incHeads = res.data.heads?.incomeHeads || [];
          const expHeads = res.data.heads?.expenseHeads || [];
          setIncomeLedgers(incHeads);
          setExpenseLedgers(expHeads);
          if (cfg?.incomeHead) setConfigIncomeSelect(cfg.incomeHead);
          if (cfg?.expenseHead) setConfigExpenseSelect(cfg.expenseHead);
        }
      })
      .catch((err) => console.error("Error fetching company config:", err));
  }, [selectedCompanyId]);

  // ---- Fetch GST states list from backend (with default fallback) ----
  useEffect(() => {
    axios
      .get(`${BASE_API_URL}/invoice/gst-states`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setGstStates(res.data.data);
        }
      })
      .catch((err) => {
        console.warn("Using default GST states fallback:", err.message);
      });
  }, [BASE_API_URL]);

  // ---- Fetch all company uninvoiced duty slips to compute per-party slip counts ----
  useEffect(() => {
    if (!selectedCompanyId) return;
    axios
      .get(`${BASE_API_URL}/invoice/duty-slips/uninvoiced/${selectedCompanyId}`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setAllCompanyDutySlips(res.data.data);
        }
      })
      .catch((err) => console.warn("Could not fetch company duty slips:", err));
  }, [selectedCompanyId, BASE_API_URL]);

  // ---- Fetch existing invoices list for modify mode selector ----
  useEffect(() => {
    if (!isModifyMode || !selectedCompanyId) return;
    axios
      .get(`${BASE_API_URL}/invoice/get-invoices/${selectedCompanyId}`)
      .then((res) => {
        if (res.data?.success) {
          setExistingInvoices(res.data.data || []);
        }
      })
      .catch((err) => console.error("Error fetching existing invoices list:", err));
  }, [isModifyMode, selectedCompanyId, BASE_API_URL]);

  // ---- Load invoice for modification ----
  const loadInvoiceForModification = async (invNo) => {
    if (!invNo) return;
    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    const CompID = company?.id || selectedCompanyId;
    if (!CompID) return;
    setIsInvoiceLoading(true);
    try {
      const res = await axios.get(
        `${BASE_API_URL}/invoice/details/${CompID}?invoiceNo=${encodeURIComponent(invNo)}`
      );
      if (res.data?.success && res.data?.data) {
        const inv = res.data.data;
        const invDate = toInputDate(inv.InvoiceDate);
        setLoadedInvoiceId(inv.InvoiceID || inv.ID || null);
        setFormData({
          invoiceNo: inv.InvoiceNoDisplay || inv.InvoiceNoUnique || inv.InvoiceNo || invNo,
          dutySlipNo: inv.DutySlipNo ? String(inv.DutySlipNo) : "",
          invoiceDate: invDate,
          vehicleNo: inv.VehicleNo || "",
          vehicleType: inv.VehicleType || "",
          driverName: inv.DriverName || "",
          reverseCharge:
            inv.ReverseCharges === 1 ||
            inv.ReverseCharges === true ||
            inv.ReverseCharge === "Yes" ||
            inv.ReverseCharge === 1
              ? "Yes"
              : "No",
          partyName: cleanPartyOrAddress(inv.PartyName || ""),
          guest: inv.Guest || "",
          bookedBy: inv.BookedBy || "",
          address: cleanPartyOrAddress(inv.PartyAddress || inv.Address || inv.clientAddress || ""),
          state: inv.State || inv.StateName || inv.state || "",
          stateCode: inv.StateCode || inv.stateCode || "",
          gstin: inv.PartyGSTIN || inv.GSTIN || inv.GstNo || inv.gstin || "",
          lessAdvance: inv.Discount || inv.Advance || "",
          headCode: inv.HeadCode || inv.PartyCode || null,
          partyCode: inv.HeadCode || inv.PartyCode || null,
        });

        if (Array.isArray(inv.items) && inv.items.length > 0) {
          const loadedItems = inv.items.map((it) => {
            let linkedDutySlipIds = [];
            if (Array.isArray(it.linkedDutySlipIds) && it.linkedDutySlipIds.length > 0) {
              linkedDutySlipIds = it.linkedDutySlipIds.map(Number);
            } else if (it.particulars && typeof it.particulars === "string") {
              if (it.particulars.startsWith("{") || it.particulars.startsWith("[")) {
                try {
                  const pObj = JSON.parse(it.particulars);
                  if (Array.isArray(pObj)) {
                    pObj.forEach((p) => {
                      if (p && p.slip) linkedDutySlipIds.push(Number(p.slip));
                    });
                  } else if (pObj && pObj.slip) {
                    linkedDutySlipIds.push(Number(pObj.slip));
                  }
                } catch (_) {}
              } else {
                const match = it.particulars.match(/#(\d+)/);
                if (match) linkedDutySlipIds.push(Number(match[1]));
              }
            }
            if (linkedDutySlipIds.length === 0 && Array.isArray(inv.dutySlipIds) && inv.dutySlipIds.length > 0) {
              linkedDutySlipIds = inv.dutySlipIds.map(Number);
            }

            const itemSvc = it.service || it.ServiceName || it.ServiceLabel || "";
            const itemSvcLower = itemSvc.trim().toLowerCase();
            const matchedSvc = services.find((s) => (s.ServiceName || "").trim().toLowerCase() === itemSvcLower);
            const effectiveTaxRate = matchedSvc && matchedSvc.TaxRate !== undefined && matchedSvc.TaxRate !== null
              ? Number(matchedSvc.TaxRate)
              : (it.TaxRate !== undefined && it.TaxRate !== null ? Number(it.TaxRate) : 0);
            const effectiveTaxName = matchedSvc?.TaxName || it.TaxName || "";
            const effectiveTaxId = matchedSvc?.TaxID || it.TaxID || null;
            const itemAmount = Number(it.amount || it.Amount || 0);
            const computedTaxAmount = Math.round((itemAmount * (effectiveTaxRate / 100)) * 100) / 100;

            return {
              date: toInputDate(it.date || it.OpeningDate || invDate),
              openingDate: toInputDate(it.OpeningDate || it.date || invDate),
              closingDate: toInputDate(it.ClosingDate || it.date || invDate),
              service: itemSvc,
              Particulars: it.particulars || it.Particulars || "",
              quantity: it.quantity || it.Qty || 1,
              unit: it.unit || it.Units || "Trip",
              rate: it.rate || it.Rate || 0,
              amount: itemAmount,
              HSNCODE: it.HSNCODE || matchedSvc?.HSNCODE || "",
              TaxRate: effectiveTaxRate,
              TaxAmount: computedTaxAmount,
              TaxName: effectiveTaxName,
              TaxID: effectiveTaxId,
              isAutoGenerated: false,
              linkedDutySlipIds,
              linkedPackageId: null,
            };
          });

          const confirmedRows = {};
          loadedItems.forEach((it, idx) => {
            if (isDutySlipService(it.service) || (it.linkedDutySlipIds && it.linkedDutySlipIds.length > 0)) {
              confirmedRows[idx] = true;
            }
          });
          setConfirmedDutySlipRows(confirmedRows);
          setItems([...loadedItems, blankInvoiceItem()]);
        }

        // In modify mode, preserve the invoice's saved manual address, GSTIN, and state.
        // Do NOT call loadPartyDetails here, which would clobber manual modifications with party defaults.
        showToast.info(`Loaded Invoice #${inv.InvoiceNoDisplay || inv.InvoiceNo || invNo} for modification.`);
      } else {
        showToast.error("Invoice details not found!");
      }
    } catch (err) {
      console.error("Error loading invoice for modification:", err);
      showToast.error("Failed to load invoice details!");
    } finally {
      setIsInvoiceLoading(false);
    }
  };

  useEffect(() => {
    if (editParam) {
      setEditingInvoiceNo(editParam);
      loadInvoiceForModification(editParam);
    }
  }, [editParam, selectedCompanyId]);

  // ---- Auto‑fetch next invoice number (only when creating new invoice) ----
  useEffect(() => {
    if (isModifyMode) return;
    const fetchInvoiceNumber = async () => {
      const company = JSON.parse(localStorage.getItem("selectedCompany"));
      const CompID = company?.id;
      if (!CompID || manualInvoice) return;
      try {
        let compCode = company?.CompanyCode || "";
        if (!compCode) {
          try {
            const compRes = await axios.get(`${BASE_API_URL}/invoice/company/${CompID}`);
            if (compRes.data.success && compRes.data.data?.CompanyCode) {
              compCode = compRes.data.data.CompanyCode;
              const updated = { ...company, CompanyCode: compCode };
              localStorage.setItem("selectedCompany", JSON.stringify(updated));
            }
          } catch (_) {}
        }

        const res = await axios.get(`${BASE_API_URL}/invoice/next-invoice/${CompID}`);
        if (res.data.success) {
          const displayNo = res.data.nextInvoiceDisplay || String(res.data.nextInvoiceNo || "1");
          setFormData((prev) => ({ ...prev, invoiceNo: displayNo }));
        }
      } catch (err) {
        console.error("Error fetching next invoice number:", err);
      }
    };
    fetchInvoiceNumber();

    const handleStorageChange = (e) => {
      if (e.key === "selectedCompany") {
        fetchInvoiceNumber();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [manualInvoice, selectedCompanyId, isModifyMode]);

  // ---- Load party details ----
  const loadPartyDetails = async (headName, options = {}) => {
    if (!headName) return;
    try {
      const company = JSON.parse(localStorage.getItem("selectedCompany"));
      if (!company || !company.id) {
        showToast.error("Please select a company first");
        return;
      }
      const url = `${BASE_API_URL}/invoice/dropdowns/details/${company.id}?headName=${encodeURIComponent(headName)}`;
      const res = await axios.get(url);
      if (res.data.success) {
        const p = res.data.data;
        setFormData((prev) => ({
          ...prev,
          address: options.preserveAddress && prev.address
            ? prev.address
            : cleanPartyOrAddress(p.ADDRESS1 || p.Address || ""),
          gstin: options.preserveGstin && prev.gstin
            ? prev.gstin
            : (p.GSTNO || ""),
          stateCode: options.preserveState && prev.stateCode
            ? prev.stateCode
            : (p.StateCode || ""),
          state: options.preserveState && prev.state
            ? prev.state
            : (p.StateName || ""),
        }));
      }
    } catch (err) {
      console.error("Error fetching party details:", err);
      showToast.error("Failed to fetch party details");
    }
  };

  // ---- Party handler (supports react-select & legacy select) ----
  const handlePartyChange = async (selectedOption) => {
    const headName = cleanPartyOrAddress(selectedOption ? selectedOption.value : "");
    const headCode = selectedOption?.headCode || selectedOption?.id || selectedOption?.raw?.HEAD_CODE || null;
    setFormData((prev) => ({ ...prev, partyName: headName, headCode, partyCode: headCode }));
    if (headName) {
      await loadPartyDetails(headName);
    } else {
      setFormData((prev) => ({
        ...prev,
        headCode: null,
        partyCode: null,
        address: "",
        gstin: "",
        stateCode: "",
        state: "",
      }));
    }
  };

  const handlePartySelect = async (e) => {
    const headName = cleanPartyOrAddress(e?.target?.value !== undefined ? e.target.value : e);
    setFormData((prev) => ({ ...prev, partyName: headName }));
    if (headName) await loadPartyDetails(headName);
  };

  // ---- GST State & State Code handlers (syncs both fields) ----
  const handleStateChange = (selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        state: selectedOption.stateName,
        stateCode: selectedOption.stateCode,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        state: "",
        stateCode: "",
      }));
    }
  };

  const handleStateCodeChange = (selectedOption) => {
    if (selectedOption) {
      setFormData((prev) => ({
        ...prev,
        state: selectedOption.stateName,
        stateCode: selectedOption.stateCode,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        state: "",
        stateCode: "",
      }));
    }
  };

  // ---- Vehicle handler (supports react-select) ----
  const handleVehicleChange = async (selectedOption) => {
    const vehicleNo = selectedOption ? selectedOption.value : "";
    setFormData((prev) => ({
      ...prev,
      vehicleNo,
      vehicleType: selectedOption?.vehicleType || prev.vehicleType,
    }));
    if (vehicleNo) {
      try {
        const res = await axios.get(
          `${BASE_API_URL}/invoice/vecicles/details?vehicleNo=${encodeURIComponent(vehicleNo)}`
        );
        if (res.data.success && res.data.data) {
          setFormData((prev) => ({ ...prev, vehicleType: res.data.data.VehicleTypeName || "" }));
        }
      } catch (err) {
        console.error("Error fetching vehicle details:", err);
      }
    } else {
      setFormData((prev) => ({ ...prev, vehicleType: "" }));
    }
  };

  const handleVehicleSelect = async (e) => {
    const vehicleNo = e?.target?.value || e;
    setFormData((prev) => ({ ...prev, vehicleNo }));
    if (vehicleNo) {
      try {
        const res = await axios.get(
          `${BASE_API_URL}/invoice/vecicles/details?vehicleNo=${encodeURIComponent(vehicleNo)}`
        );
        if (res.data.success && res.data.data) {
          setFormData((prev) => ({ ...prev, vehicleType: res.data.data.VehicleTypeName || "" }));
        }
      } catch (err) {
        console.error("Error fetching vehicle details:", err);
        setFormData((prev) => ({ ...prev, vehicleType: "" }));
      }
    } else {
      setFormData((prev) => ({ ...prev, vehicleType: "" }));
    }
  };

  // ---- Memoized Party Options with Duty Slip Counts & Filter ----
  const partySlipCountMap = useMemo(() => {
    const map = {};
    (allCompanyDutySlips || []).forEach((s) => {
      const pName = (s.PartyName || "").trim().toLowerCase();
      if (pName) {
        map[pName] = (map[pName] || 0) + 1;
      }
    });
    return map;
  }, [allCompanyDutySlips]);

  const partiesWithDutySlipsCount = useMemo(() => {
    return Object.keys(partySlipCountMap).length;
  }, [partySlipCountMap]);

  const displayedPartyOptions = useMemo(() => {
    let list = parties || [];
    if (partyFilter === "with_duty_slip") {
      list = list.filter((p) => {
        const pName = (p.HEADName || "").trim().toLowerCase();
        return (partySlipCountMap[pName] || 0) > 0;
      });
    }
    const opts = list.map((p) => {
      const pName = p.HEADName || "";
      const count = partySlipCountMap[pName.trim().toLowerCase()] || 0;
      return {
        value: pName,
        label: count > 0 ? `${pName} (${count} slip${count > 1 ? "s" : ""})` : pName,
        raw: p,
        count,
      };
    });
    if (formData.partyName && !opts.some((o) => o.value === formData.partyName)) {
      const count = partySlipCountMap[formData.partyName.trim().toLowerCase()] || 0;
      opts.unshift({
        value: formData.partyName,
        label: count > 0 ? `${formData.partyName} (${count} slip${count > 1 ? "s" : ""})` : formData.partyName,
        raw: null,
        count,
      });
    }
    return opts;
  }, [parties, partyFilter, partySlipCountMap, formData.partyName]);

  const selectedPartyOption = useMemo(() => {
    if (!formData.partyName) return null;
    return (
      displayedPartyOptions.find((o) => o.value === formData.partyName) || {
        value: formData.partyName,
        label: formData.partyName,
      }
    );
  }, [displayedPartyOptions, formData.partyName]);

  // ---- Memoized GST State Options & Selected State ----
  const stateOptions = useMemo(() => {
    return (gstStates || []).map((s) => ({
      value: s.State,
      label: `${s.State} (${s.Code})`,
      stateName: s.State,
      stateCode: s.Code,
    }));
  }, [gstStates]);

  const selectedStateOption = useMemo(() => {
    if (!formData.state && !formData.stateCode) return null;
    const sNorm = (formData.state || "").trim().toLowerCase();
    const cNorm = (formData.stateCode || "").trim().padStart(2, "0");
    return (
      stateOptions.find(
        (o) =>
          (sNorm && o.stateName.trim().toLowerCase() === sNorm) ||
          (cNorm && o.stateCode.trim().padStart(2, "0") === cNorm)
      ) ||
      (formData.state
        ? {
            value: formData.state,
            label: formData.stateCode ? `${formData.state} (${formData.stateCode})` : formData.state,
            stateName: formData.state,
            stateCode: formData.stateCode || "",
          }
        : null)
    );
  }, [stateOptions, formData.state, formData.stateCode]);

  const stateCodeOptions = useMemo(() => {
    return (gstStates || []).map((s) => ({
      value: s.Code,
      label: `${s.Code} - ${s.State}`,
      stateName: s.State,
      stateCode: s.Code,
    }));
  }, [gstStates]);

  const selectedStateCodeOption = useMemo(() => {
    if (!formData.stateCode && !formData.state) return null;
    const cNorm = (formData.stateCode || "").trim().padStart(2, "0");
    const sNorm = (formData.state || "").trim().toLowerCase();
    return (
      stateCodeOptions.find(
        (o) =>
          (cNorm && o.stateCode.trim().padStart(2, "0") === cNorm) ||
          (sNorm && o.stateName.trim().toLowerCase() === sNorm)
      ) ||
      (formData.stateCode
        ? {
            value: formData.stateCode,
            label: formData.state ? `${formData.stateCode} - ${formData.state}` : formData.stateCode,
            stateName: formData.state || "",
            stateCode: formData.stateCode,
          }
        : null)
    );
  }, [stateCodeOptions, formData.stateCode, formData.state]);

  // ---- Memoized Vehicle Options & Selected Vehicle ----
  const vehicleOptions = useMemo(() => {
    return (vehicles || []).map((v) => ({
      value: v.VehicleNo,
      label: v.VehicleTypeName ? `${v.VehicleNo} (${v.VehicleTypeName})` : v.VehicleNo,
      vehicleType: v.VehicleTypeName || "",
      raw: v,
    }));
  }, [vehicles]);

  const selectedVehicleOption = useMemo(() => {
    if (!formData.vehicleNo) return null;
    return (
      vehicleOptions.find((o) => o.value === formData.vehicleNo) || {
        value: formData.vehicleNo,
        label: formData.vehicleNo,
      }
    );
  }, [vehicleOptions, formData.vehicleNo]);

  // ---- Generic input change handler (for text/date inputs) ----
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "invoiceDate") {
      setFormData((prev) => {
        const num = extractInvoiceNumeric(prev.invoiceNo);
        const pfx = (companyConfig?.prefix && companyConfig.prefix.trim()) ? companyConfig.prefix.trim() : "";
        const sfx = (companyConfig?.suffix && companyConfig.suffix.trim()) ? companyConfig.suffix.trim() : "";
        let updatedInvNo = prev.invoiceNo;
        if (num) {
          updatedInvNo = `${pfx}${num}${sfx}`;
        }
        return {
          ...prev,
          invoiceDate: value,
          invoiceNo: updatedInvNo,
        };
      });
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---- Compute expected prefix & suffix format from company config ----
  const computedPrefixSuffixNo = useMemo(() => {
    if (!formData.invoiceNo) return "";
    const num = extractInvoiceNumeric(formData.invoiceNo);
    if (!num) return "";
    const pfx = (companyConfig?.prefix && companyConfig.prefix.trim()) ? companyConfig.prefix.trim() : "";
    const sfx = (companyConfig?.suffix && companyConfig.suffix.trim()) ? companyConfig.suffix.trim() : "";
    return `${pfx}${num}${sfx}`;
  }, [formData.invoiceNo, companyConfig]);

  const canUpdatePrefixSuffix = Boolean(
    isModifyMode &&
    formData.invoiceNo &&
    computedPrefixSuffixNo &&
    formData.invoiceNo.trim() !== computedPrefixSuffixNo.trim()
  );

  const handleUpdatePrefixSuffix = () => {
    if (!computedPrefixSuffixNo) return;
    setFormData((prev) => ({ ...prev, invoiceNo: computedPrefixSuffixNo }));
    setManualInvoice(true);
    showToast.success(`Updated Invoice No to ${computedPrefixSuffixNo}`);
  };

  // ---- Fetch uninvoiced duty slips ----
  useEffect(() => {
    if (!selectedCompanyId) {
      setUninvoicedDutySlips([]);
      return;
    }
    const params = new URLSearchParams();
    if (formData.partyName) params.append("partyName", formData.partyName);
    if (loadedInvoiceId) params.append("includeInvoiceId", loadedInvoiceId);
    if (formData.invoiceNo) params.append("invoiceNo", formData.invoiceNo);
    const query = params.toString() ? `?${params.toString()}` : "";

    axios
      .get(`${BASE_API_URL}/invoice/duty-slips/uninvoiced/${selectedCompanyId}${query}`)
      .then((res) => {
        if (res.data?.success) {
          setUninvoicedDutySlips(res.data.data || []);
        } else {
          setUninvoicedDutySlips([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching uninvoiced duty slips:", err);
        setUninvoicedDutySlips([]);
      });
  }, [BASE_API_URL, selectedCompanyId, formData.partyName, loadedInvoiceId, formData.invoiceNo]);

  // ---- Fetch fare packages ----
  useEffect(() => {
    if (!selectedCompanyId) {
      setFarePackages([]);
      return;
    }
    const query = formData.vehicleType ? `?vehicleType=${encodeURIComponent(formData.vehicleType)}` : "";
    axios
      .get(`${BASE_API_URL}/invoice/packages/${selectedCompanyId}${query}`)
      .then((res) => {
        if (res.data?.success) {
          setFarePackages(res.data.data || []);
        } else {
          setFarePackages([]);
        }
      })
      .catch((err) => {
        console.error("Error fetching packages:", err);
        setFarePackages([]);
      });
  }, [BASE_API_URL, selectedCompanyId, formData.vehicleType]);

  // ---- Per-row Duty Slip & Package helpers ----
  const getUsedDutySlipIdsExcluding = (excludeIndex) => {
    const used = new Set();
    items.forEach((it, i) => {
      if (i === excludeIndex) return;
      (it.linkedDutySlipIds || []).forEach((id) => used.add(id));
    });
    return used;
  };

  const getAvailableDutySlipsForRow = (index) => {
    const used = getUsedDutySlipIdsExcluding(index);
    return uninvoicedDutySlips.filter((s) => !used.has(s.DutySlipID));
  };

  const getRowSlipStats = (item) => {
    let slips = uninvoicedDutySlips.filter((s) =>
      (item.linkedDutySlipIds || []).includes(s.DutySlipID)
    );
    if (!slips.length && item.Particulars) {
      try {
        const p = typeof item.Particulars === "string" && (item.Particulars.startsWith("{") || item.Particulars.startsWith("[")) ? JSON.parse(item.Particulars) : null;
        if (Array.isArray(p)) {
          slips = p.map((sp) => ({
            DutySlipID: sp.slip,
            SlipDate: sp.date || item.date,
            PackageName: sp.package,
            TotalKM: sp.km,
            TotalTime: sp.time,
            isReceivable: true,
            FareType: "receivable",
            dateWiseDetails: Array.isArray(sp.days) ? sp.days.map((d) => ({
              date: d.date,
              totalKm: d.km,
              totalTime: d.time ? `${Math.floor(d.time)}:${Math.round((d.time % 1) * 60)}` : "0:00",
              packageName: d.package,
              tripDetails: d.route,
              baseFare: d.baseFare,
              kmRate: d.kmRate,
              timeRate: d.timeRate,
              incKM: d.incKM,
              incHrs: d.incHrs,
            })) : [],
          }));
        } else if (p && p.slip) {
          slips = [{
            DutySlipID: p.slip,
            SlipDate: item.date,
            PackageName: p.package,
            TotalKM: p.km,
            TotalTime: p.time,
            dateWiseDetails: Array.isArray(p.days) ? p.days.map((d) => ({
              date: d.date,
              totalKm: d.km,
              totalTime: d.time ? `${Math.floor(d.time)}:${Math.round((d.time % 1) * 60)}` : "0:00",
              packageName: d.package,
              tripDetails: d.route,
              baseFare: d.baseFare,
              kmRate: d.kmRate,
              timeRate: d.timeRate,
              incKM: d.incKM,
              incHrs: d.incHrs,
            })) : [],
          }];
        }
      } catch (_) {}
    }
    const totalKm = slips.reduce((sum, s) => sum + (s.TotalKM || 0), 0);
    const totalTime = slips.reduce((sum, s) => sum + (s.TotalTime || 0), 0);
    const totalAmount = slips.reduce((sum, s) => sum + calculateSlipTotal(s), 0);
    const uniqueDays = new Set(slips.map((s) => toInputDate(s.SlipDate))).size;
    return { slips, totalKm, totalTime, totalAmount, uniqueDays };
  };

// ----- day-wise packages display string -----
const getDayPackagesString = (slip) => {
  if (!slip) return "";
  if (Array.isArray(slip.dateWiseDetails) && slip.dateWiseDetails.length) {
    const fallbackPkg = (slip.PackageName || slip.packageName || "").trim();
    const pkgs = slip.dateWiseDetails
      .map((d, i) => {
        const pkg = (d.packageName || d.PackageName || d.DayPackageName || fallbackPkg).trim();
        return pkg ? `Day ${i + 1}: ${pkg}` : null;
      })
      .filter(Boolean);
    if (pkgs.length) return pkgs.join(", ");
  }
  return (slip.PackageName || slip.packageName || "").trim();
};

const buildDutySlipRow = (slipIds, sourceSlips = uninvoicedDutySlips) => {
  const slips = (sourceSlips || []).filter((s) => slipIds.includes(s.DutySlipID));
  if (!slips.length) {
    const t = new Date().toISOString().split("T")[0];
    return { date: t, openingDate: t, closingDate: t, amount: 0, Particulars: "", rate: 0 };
  }

  const openingDates = slips
    .map((s) => toInputDate(s.OpeningDate || s.SlipDate))
    .filter(Boolean)
    .sort();
  const closingDates = slips
    .map((s) => toInputDate(s.ClosingDate || s.SlipDate))
    .filter(Boolean)
    .sort();
  const openingDate = openingDates[0] || new Date().toISOString().split("T")[0];
  const closingDate = closingDates[closingDates.length - 1] || openingDate;

  let mainFuelRate = 0;
  let totalSlipsCalculatedAmount = 0;

  // Build rich JSON Particulars per slip
  const particulars = slips.map((s) => {
    const details = Array.isArray(s.dateWiseDetails) ? s.dateWiseDetails : [];
    const tripLegs = details
      .map((d) => (d.tripDetails || d.TripDetails || d.route || d.Route || "").trim())
      .filter(Boolean)
      .join(", ") || (s.tripDetails || s.TripDetails || s.Route || s.route || "").trim();
    const trip = tripLegs || getTripDetailsString(s);

    const parking = Number(s.Parking || s.parking) || 0;
    const driverAllowance = Number(s.DriverAllowance || s.driverAllowance) || 0;
    const tollTax = Number(s.TollTax || s.tollTax) || 0;
    const nightCharges = Number(s.NightCharges || s.nightCharges) || 0;

    const dateRange = formatDateRangeDisplay(s.OpeningDate || s.SlipDate, s.ClosingDate || s.SlipDate);

    let totalIncKM = details.reduce((sum, d) => sum + (calculateDutySlipDayMetrics(d, s, farePackages).incKM || 0), 0);
    let totalIncMins = details.reduce((sum, d) => sum + (calculateDutySlipDayMetrics(d, s, farePackages).incHrs * 60 || 0), 0);
    let totalActualKM = 0;
    let totalActualMins = 0;

    // Build per-day breakdown array
    const fallbackSlipBase = Number(s.BaseFare || s.baseFare || 0);
    const daysArr = details.map((d, idx) => {
      const m = calculateDutySlipDayMetrics(d, s, farePackages);
      const close = Number(d.kmClose || d.kmCloseGarage || 0);
      const start = Number(d.kmStart || d.kmStartGarage || d.kmStartUser || 0);
      const calcKm = (close > start && close > 0) ? (close - start) : 0;
      const dayKm = Number(d.totalKm ?? d.totalKM ?? d.TotalKM ?? d.km ?? 0) || calcKm;
      const dayMins = parseTimeToMins(d.totalTime);

      totalActualKM += dayKm;
      totalActualMins += dayMins;

      const rawDayBase = Number(m.baseFare || 0);
      const dayBase = rawDayBase > 0 ? rawDayBase : (details.length === 1 ? fallbackSlipBase : (fallbackSlipBase / (details.length || 1)));

      return {
        day: idx + 1,
        date: d.date ? toInputDate(d.date) : "",
        package: (m.pkgName || "").toUpperCase(),
        route: (d.tripDetails || d.TripDetails || d.route || d.Route || "").trim(),
        km: dayKm,
        time: Math.round((dayMins / 60) * 100) / 100,
        baseFare: dayBase,
        incKM: m.incKM,
        incHrs: m.incHrs,
        packageType: m.packageType,
        extraKM: 0,
        kmRate: m.kmRate,
        kmCharge: 0,
        extraMins: 0,
        timeRate: m.timeRate,
        timeCharge: 0,
        dayTotal: dayBase,
      };
    });

    const rawTopKm = Number(s.TotalKM ?? s.totalKM ?? s.totalKm ?? s.km ?? 0);
    const firstStart = Number(details[0]?.kmStart || details[0]?.kmStartGarage || details[0]?.kmStartUser || 0);
    const lastClose = Number(details[details.length - 1]?.kmClose || details[details.length - 1]?.kmCloseGarage || 0);
    const tripSpan = (lastClose > firstStart && firstStart > 0) ? (lastClose - firstStart) : 0;

    let realTripKm = rawTopKm > 0 ? rawTopKm : (tripSpan > 0 ? tripSpan : 0);
    if (realTripKm === 0 && totalActualKM > 0) {
      realTripKm = totalActualKM;
    }

    const activeIncKM = details.length > 0 ? totalIncKM : (Number(s.IncludedKM) || 0);
    const activeIncMins = details.length > 0 ? totalIncMins : ((Number(s.IncludedHrs) || 0) * 60);
    const activeActualKM = realTripKm;
    const activeActualMins = details.length > 0 ? totalActualMins : ((Number(s.TotalTime) || 0) * 60);

    const extraKM = Math.max(0, activeActualKM - activeIncKM);
    const extraMinutes = Math.max(0, activeActualMins - activeIncMins);

    const kmRate = daysArr.find((r) => r.kmRate > 0)?.kmRate || (Number(s.FuelRateKM) || 0);
    const timeRate = daysArr.find((r) => r.timeRate > 0)?.timeRate || (Number(s.TimeRatePM) || 0);
    const isSlipOutstation = checkIsOutstation(s, farePackages);
    let baseFare = (daysArr.length > 0 && daysArr.reduce((sum, d) => sum + d.baseFare, 0) > 0)
      ? daysArr.reduce((sum, d) => sum + d.baseFare, 0)
      : (Number(s.BaseFare || s.baseFare || 0));
    if (isSlipOutstation && baseFare === 0) {
      const effKm = (Number(s.IncludedKM) || 0) > 0 ? Number(s.IncludedKM) : 250;
      const effRate = kmRate > 0 ? kmRate : (Number(s.FuelRateKM || 0) + Number(s.LubRateKM || 0));
      baseFare = effKm * effRate;
    }

    const pkgLabel = daysArr.length > 1
      ? daysArr.map((d) => d.package).filter(Boolean).join(", ")
      : (s.PackageName || s.packageName || "Package").toUpperCase();

    const closeKm = Number(s.KMClose || s.kmEnd || 0);
    const startKm = Number(s.KMStart || s.kmStart || 0);
    const calcSlipKm = (closeKm > startKm && closeKm > 0) ? (closeKm - startKm) : 0;

    const slipTotalKm = activeActualKM > 0
      ? activeActualKM
      : (Number(s.TotalKM ?? s.totalKM ?? s.totalKm ?? s.km ?? 0) || calcSlipKm);
    const slipTotalTime = s.TotalTime ?? s.totalTime ?? 0;

    // Detect packageType: 0 = Local, 1 = Outstation
    const detectedPkgType = isSlipOutstation ? 1 : 0;

    const slipFuelRate = kmRate > 0 ? kmRate : 0;
    if (slipFuelRate > 0 && mainFuelRate === 0) mainFuelRate = slipFuelRate;

    const kmCharge = Math.round(extraKM * kmRate * 100) / 100;
    const timeCharge = Math.round((extraMinutes / 60) * timeRate * 100) / 100;
    const slipTotalAmount = Math.round((baseFare + kmCharge + timeCharge + parking + driverAllowance + tollTax + nightCharges) * 100) / 100;

    totalSlipsCalculatedAmount += slipTotalAmount;

    return JSON.stringify({
      pkg: pkgLabel,
      slip: s.DutySlipID,
      dateRange,
      route: trip,
      vehicleType: s.VehicleType || "",
      vehicleNo: s.VehicleNo || "",
      totalKM: slipTotalKm,
      totalTime: slipTotalTime,
      baseFare,
      incKM: activeIncKM,
      extraKM,
      kmRate,
      kmCharge,
      extraMins: extraMinutes,
      timeRate,
      timeCharge,
      parking: Math.round(parking * 100) / 100,
      driverAllowance: Math.round(driverAllowance * 100) / 100,
      tollTax: Math.round(tollTax * 100) / 100,
      amount: slipTotalAmount,
      packageType: detectedPkgType,
      days: daysArr.length > 0 ? daysArr : undefined,
    });
  }).join(" || ");

  const amount = totalSlipsCalculatedAmount > 0
    ? totalSlipsCalculatedAmount
    : slips.reduce((sum, s) => sum + calculateSlipTotal(s, farePackages), 0);

  const displayRate = amount;

  return {
    date: openingDate,
    openingDate,
    closingDate,
    amount,
    rate: displayRate,
    quantity: 1,
    unit: "Trip",
    service: TRANSPORT_WITH_DUTY_SLIP,
    Particulars: particulars,
  };
};

  // ── Auto-fill header from first selected duty slip ──
  const autoFillFromDutySlips = (slipIds) => {
    const slips = uninvoicedDutySlips.filter((s) => slipIds.includes(s.DutySlipID));
    if (!slips.length) return;
    const first = slips[0];

    const partyName = first.PartyName || "";
    const vehicleType = first.VehicleType || "";
    const vehicleNo = first.VehicleNo || "";
    const driverName = first.DriverName || "";
    const guest = first.ClientName || "";
    const slipNo = first.DutySlipID || first.DutySlipNo;
    const slipDate = toInputDate(first.OpeningDate || first.SlipDate);
    const manualAddress = cleanPartyOrAddress(first.ClientAddress || first.clientAddress || first.address || "");
    const partyAddress = cleanPartyOrAddress(first.PartyAddress || "");
    const finalAddress = manualAddress || partyAddress;

    setFormData((prev) => ({
      ...prev,
      ...(slipNo ? { dutySlipNo: String(slipNo) } : {}),
      ...(partyName ? { partyName: cleanPartyOrAddress(partyName) } : {}),
      ...(finalAddress ? { address: finalAddress } : {}),
      ...(vehicleType ? { vehicleType } : {}),
      ...(vehicleNo ? { vehicleNo } : {}),
      ...(driverName ? { driverName } : {}),
      ...(guest ? { guest } : {}),
      ...(first.PartyGSTIN ? { gstin: first.PartyGSTIN } : {}),
      ...(first.PartyState ? { state: first.PartyState } : {}),
      ...(first.PartyStateCode ? { stateCode: first.PartyStateCode } : {}),
    }));

    // Also load address/GSTIN/state from party, preserving manual address if present on slip
    if (partyName) {
      loadPartyDetails(partyName, {
        preserveAddress: Boolean(manualAddress),
        preserveGstin: Boolean(first.PartyGSTIN),
        preserveState: Boolean(first.PartyState),
      });
    }
  };

  // ── Auto-select duty slip passed in query parameters (?dutySlipId=...) ──
  useEffect(() => {
    if (!dutySlipIdParam || preselectedSlipHandled.current || isModifyMode) return;
    const slipId = Number(dutySlipIdParam);
    if (!slipId) return;

    const availableSlips = [
      ...(uninvoicedDutySlips || []),
      ...(allCompanyDutySlips || []),
    ];
    const targetSlip = availableSlips.find((s) => Number(s.DutySlipID) === slipId);

    if (targetSlip) {
      preselectedSlipHandled.current = true;
      const party = cleanPartyOrAddress(targetSlip.PartyName || "");
      if (party) {
        const manualAddr = cleanPartyOrAddress(targetSlip.ClientAddress || targetSlip.clientAddress || targetSlip.address || "");
        const finalAddr = manualAddr || cleanPartyOrAddress(targetSlip.PartyAddress || "");
        setFormData((prev) => ({
          ...prev,
          partyName: party,
          address: finalAddr || prev.address || "",
          dutySlipNo: String(targetSlip.DutySlipID || slipId),
          vehicleType: targetSlip.VehicleType || prev.vehicleType,
          vehicleNo: targetSlip.VehicleNo || prev.vehicleNo,
          driverName: targetSlip.DriverName || prev.driverName,
          guest: targetSlip.ClientName || prev.guest,
          ...(targetSlip.PartyGSTIN ? { gstin: targetSlip.PartyGSTIN } : {}),
          ...(targetSlip.PartyState ? { state: targetSlip.PartyState } : {}),
          ...(targetSlip.PartyStateCode ? { stateCode: targetSlip.PartyStateCode } : {}),
        }));
        loadPartyDetails(party, {
          preserveAddress: Boolean(manualAddr),
          preserveGstin: Boolean(targetSlip.PartyGSTIN),
          preserveState: Boolean(targetSlip.PartyState),
        });
      }

      const builtRow = buildDutySlipRow([slipId], [targetSlip, ...(uninvoicedDutySlips || [])]);
      setItems((prev) => {
        const copy = [...prev];
        copy[0] = {
          ...copy[0],
          ...builtRow,
          service: TRANSPORT_WITH_DUTY_SLIP,
          quantity: 1,
          unit: "Trip",
          rate: builtRow.amount,
          amount: builtRow.amount,
          linkedDutySlipIds: [slipId],
        };
        if (!copy[0].TaxRate) {
          copy[0].TaxRate = 5;
          copy[0].TaxName = "GST 5%";
        }
        copy[0].TaxAmount = Math.round(((copy[0].amount * copy[0].TaxRate) / 100) * 100) / 100;
        if (copy.length === 1) {
          copy.push(blankInvoiceItem());
        }
        return copy;
      });
      setConfirmedDutySlipRows((prev) => ({ ...prev, 0: true }));
    } else if (selectedCompanyId) {
      axios
        .get(`${BASE_API_URL}/dutyslip/dutyslip?compId=${selectedCompanyId}`)
        .then((res) => {
          if (res.data?.success && Array.isArray(res.data.data)) {
            const fetched = res.data.data.find(
              (s) => Number(s.DutyslipID || s.id || s.DutySlipID) === slipId
            );
            if (fetched) {
              const normalized = {
                DutySlipID: Number(fetched.DutyslipID || fetched.id || fetched.DutySlipID),
                SlipDate: fetched.DutyslipDate || fetched.slipDate,
                OpeningDate: fetched.openDutyslipDate || fetched.openDate || fetched.DutyslipDate,
                ClosingDate: fetched.endDutyslipDate || fetched.closeDate || fetched.DutyslipDate,
                PartyName: fetched.PartyName || fetched.partyName,
                PartyAddress: fetched.PartyAddress || fetched.partyAddress || fetched.address,
                ClientAddress: fetched.ClientAddress || fetched.clientAddress || fetched.address,
                ClientName: fetched.ClientName || fetched.guestName,
                VehicleType: fetched.VehicleType || fetched.vehicleType,
                VehicleNo: fetched.VehicleNo || fetched.vehicleNo,
                DriverName: fetched.DriverName || fetched.driverName,
                PackageName: fetched.PackageName || fetched.packageName,
                TotalKM: fetched.TotalKM ?? fetched.totalKm,
                TotalTime: fetched.TotalTime ?? fetched.totalTime,
                dateWiseDetails: fetched.dateWiseDetails || [],
              };
              setAllCompanyDutySlips((prev) => [...prev, normalized]);
            }
          }
        })
        .catch(() => {});
    }
  }, [dutySlipIdParam, uninvoicedDutySlips, allCompanyDutySlips, isModifyMode, selectedCompanyId, BASE_API_URL]);

  const startEditingSlip = (s) => {
    setEditingSlipId(s.DutySlipID);
    const resolvedFareType = (s.FareType || s.fareType || (s.isReceivable ? "receivable" : "") || "").toLowerCase();
    const finalFareType = (resolvedFareType === "payable" || resolvedFareType === "receivable")
      ? resolvedFareType
      : ((formData.partyName || s.PartyName) ? "receivable" : "payable");

    const activeRates = (finalFareType === "payable" && s.payableRates)
      ? s.payableRates
      : ((finalFareType === "receivable" && s.receivableRates) ? s.receivableRates : s);

    const isSlipOutstation = checkIsOutstation(activeRates, farePackages);

    setSlipEditForm({
      PackageName: activeRates.PackageName || activeRates.packageName || s.PackageName || s.packageName || "",
      fareType: finalFareType,
      isOutstation: isSlipOutstation,
      BaseFare: isSlipOutstation ? 0 : (activeRates.BaseFare !== null && activeRates.BaseFare !== undefined ? activeRates.BaseFare : 0),
      IncludedKM: activeRates.IncludedKM !== null && activeRates.IncludedKM !== undefined ? activeRates.IncludedKM : 0,
      IncludedHrs: isSlipOutstation ? 0 : (activeRates.IncludedHrs !== null && activeRates.IncludedHrs !== undefined ? activeRates.IncludedHrs : 0),
      FuelRateKM: activeRates.FuelRateKM !== null && activeRates.FuelRateKM !== undefined ? activeRates.FuelRateKM : 0,
      LubRateKM: activeRates.LubRateKM !== null && activeRates.LubRateKM !== undefined ? activeRates.LubRateKM : 0,
      TimeRatePM: isSlipOutstation ? 0 : (activeRates.TimeRatePM !== null && activeRates.TimeRatePM !== undefined ? activeRates.TimeRatePM : 0),
    });
  };

  const handleSaveSlipRates = async (slipId, rowIndex) => {
    const updatedPackageName = (slipEditForm.PackageName || "").trim();
    const isOutstation = slipEditForm.isOutstation;
    const updatedBaseFare = isOutstation ? 0 : (Number(slipEditForm.BaseFare) || 0);
    const updatedIncludedKM = Number(slipEditForm.IncludedKM) || 0;
    const updatedIncludedHrs = isOutstation ? 0 : (Number(slipEditForm.IncludedHrs) || 0);
    const updatedFuelRateKM = Number(slipEditForm.FuelRateKM) || 0;
    const updatedLubRateKM = Number(slipEditForm.LubRateKM) || 0;
    const updatedTimeRatePM = isOutstation ? 0 : (Number(slipEditForm.TimeRatePM) || 0);
    const selectedFareType = slipEditForm.fareType || "receivable";

    const targetSlip = uninvoicedDutySlips.find((s) => s.DutySlipID === slipId);
    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    const CompID = company?.id || formData.compId || 1;

    // Save to database (ReceivableFare or Fares) in background
    try {
      await axios.post(`${BASE_API_URL}/fares/save-single-fare`, {
        CompID,
        PartyID: formData.partyCode || formData.headCode || targetSlip?.PartyCode || targetSlip?.PartyID || null,
        PartyName: formData.partyName || targetSlip?.PartyName || null,
        PackageName: updatedPackageName,
        VehicleType: targetSlip?.VehicleType,
        VtypeId: targetSlip?.CarId,
        BaseFare: updatedBaseFare,
        FuelRateKM: updatedFuelRateKM,
        LubRateKM: updatedLubRateKM,
        TimeRatePM: updatedTimeRatePM,
        IncludedKM: updatedIncludedKM,
        IncludedHrs: updatedIncludedHrs,
        fareType: selectedFareType,
      });
    } catch (err) {
      console.warn("Could not save single fare to DB table, applying locally:", err);
    }

    const nextSlips = uninvoicedDutySlips.map((s) => {
      if (s.DutySlipID === slipId) {
        const numDays = Array.isArray(s.dateWiseDetails) && s.dateWiseDetails.length > 0 ? s.dateWiseDetails.length : 1;
        const dayBaseFare = numDays > 1 ? Math.round((updatedBaseFare / numDays) * 100) / 100 : updatedBaseFare;
        const dayIncKM = numDays > 1 ? Math.round((updatedIncludedKM / numDays) * 100) / 100 : updatedIncludedKM;
        const dayIncHrs = numDays > 1 ? Math.round((updatedIncludedHrs / numDays) * 100) / 100 : updatedIncludedHrs;

        return {
          ...s,
          PackageName: updatedPackageName,
          packageName: updatedPackageName,
          PackageType: isOutstation ? 1 : 0,
          packageType: isOutstation ? 1 : 0,
          BaseFare: updatedBaseFare,
          baseFare: updatedBaseFare,
          IncludedKM: updatedIncludedKM,
          includedKM: updatedIncludedKM,
          IncludedHrs: updatedIncludedHrs,
          includedHrs: updatedIncludedHrs,
          FuelRateKM: updatedFuelRateKM,
          fuelRateKM: updatedFuelRateKM,
          LubRateKM: updatedLubRateKM,
          lubRateKM: updatedLubRateKM,
          TimeRatePM: updatedTimeRatePM,
          timeRatePM: updatedTimeRatePM,
          fareType: selectedFareType,
          FareType: selectedFareType,
          dateWiseDetails: Array.isArray(s.dateWiseDetails)
            ? s.dateWiseDetails.map((d) => ({
                ...d,
                packageName: updatedPackageName,
                PackageName: updatedPackageName,
                packageType: isOutstation ? 1 : 0,
                PackageType: isOutstation ? 1 : 0,
                baseFare: dayBaseFare,
                BaseFare: dayBaseFare,
                includedKM: dayIncKM,
                IncludedKM: dayIncKM,
                includedHrs: dayIncHrs,
                IncludedHrs: dayIncHrs,
                fuelRateKM: updatedFuelRateKM,
                FuelRateKM: updatedFuelRateKM,
                lubRateKM: updatedLubRateKM,
                LubRateKM: updatedLubRateKM,
                timeRatePM: updatedTimeRatePM,
                TimeRatePM: updatedTimeRatePM,
                fareType: selectedFareType,
                FareType: selectedFareType,
              }))
            : s.dateWiseDetails,
        };
      }
      return s;
    });

    setUninvoicedDutySlips(nextSlips);

    setItems((prevItems) => {
      return prevItems.map((item) => {
        if (item && item.linkedDutySlipIds && item.linkedDutySlipIds.includes(slipId)) {
          const built = buildDutySlipRow(item.linkedDutySlipIds, nextSlips);
          const taxAmt = Math.round(((built.amount * (item.TaxRate || 5)) / 100) * 100) / 100;
          return {
            ...item,
            ...built,
            TaxAmount: taxAmt,
          };
        }
        return item;
      });
    });

    setEditingSlipId(null);
    showToast.save(`Rates saved to ${selectedFareType === "receivable" ? "Receivable" : "Payable"} Fares & applied!`);
  };

  const toggleDutySlipForRow = (index, slipId) => {
    setItems((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      const already = (row.linkedDutySlipIds || []).includes(slipId);
      row.linkedDutySlipIds = already
        ? row.linkedDutySlipIds.filter((id) => id !== slipId)
        : [...(row.linkedDutySlipIds || []), slipId];

      const built = buildDutySlipRow(row.linkedDutySlipIds);
      Object.assign(row, built);
      if (!row.TaxRate) {
        row.TaxRate = 5;
        row.TaxName = "GST 5%";
      }
      row.TaxAmount = Math.round(((row.amount * row.TaxRate) / 100) * 100) / 100;
      updated[index] = row;

      if (row.linkedDutySlipIds.length > 0) {
        autoFillFromDutySlips(row.linkedDutySlipIds);
      }

      if (index === updated.length - 1 && row.linkedDutySlipIds.length > 0) {
        updated.push(blankInvoiceItem());
      }
      return updated;
    });
  };

  const selectPackageForRow = (index, value) => {
    setItems((prev) => {
      const updated = [...prev];
      const row = { ...updated[index] };
      row.linkedPackageId = value;

      if (value === "custom") {
        row.Particulars = "";
        row.amount = 0;
        row.rate = 0;
      } else {
        const pkg = farePackages.find((p) => String(p.ID) === String(value));
        if (pkg) {
          row.Particulars = pkg.PackageName || "Package";
          row.amount = Number(pkg.BaseFare) || 0;
          row.rate = Number(pkg.BaseFare) || 0;
          row.HSNCODE = pkg.HSNCODE || "";
          row.TaxRate = Number(pkg.TaxRate) || 0;
          row.TaxAmount = Math.round(((row.amount * row.TaxRate) / 100) * 100) / 100;
          row.TaxName = pkg.TaxName || "";
          row.TaxID = pkg.TaxID || null;
        }
      }
      updated[index] = row;
      if (index === updated.length - 1) updated.push(blankInvoiceItem());
      return updated;
    });
  };

  // ---- Item change handler ----
  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    if (updated[index].isAutoGenerated) return;
    updated[index][field] = value;
      if (field === "date") {
      updated[index].openingDate = value;
      updated[index].closingDate = value;
    }
    if (field === "quantity" || field === "rate" || field === "TaxRate") {
      const qty = parseFloat(field === "quantity" ? value : (updated[index].quantity || 1)) || 1;
      const rate = parseFloat(field === "rate" ? value : updated[index].rate) || 0;
      const taxRate = parseFloat(field === "TaxRate" ? value : updated[index].TaxRate) || 0;
      const amount = qty * rate;
      updated[index].amount = amount;
      updated[index].TaxAmount = (amount * taxRate) / 100;
      if (field === "TaxRate") updated[index].TaxRate = taxRate;
    }
    setItems(updated);
  };
  const handleServiceChange = (index, serviceOrName) => {
    setItems((prev) => {
      const updated = [...prev];
      if (updated[index].isAutoGenerated) return updated;

      let name = typeof serviceOrName === "object" ? serviceOrName.ServiceName : serviceOrName;
      
      const matchedSvc = typeof serviceOrName === "object"
        ? serviceOrName
        : services.find((s) => (s.ServiceName || "").trim().toLowerCase() === (name || "").trim().toLowerCase());

      const hsn = matchedSvc?.HSNCODE ?? "";
      const taxRate = matchedSvc && matchedSvc.TaxRate !== undefined && matchedSvc.TaxRate !== null ? parseFloat(matchedSvc.TaxRate) : 0;
      const taxName = matchedSvc?.TaxName || "";
      const taxId = matchedSvc?.TaxID || matchedSvc?.Tax_id || null;
      const unit = matchedSvc?.Unit || "Trip";

      updated[index] = {
        ...updated[index],
        service: name,
        HSNCODE: hsn,
        TaxRate: taxRate,
        TaxName: taxName,
        TaxID: taxId,
        unit: unit,
        linkedDutySlipIds: [],
        linkedPackageId: null,
        Particulars: "",
        amount: 0,
        rate: 0,
        TaxAmount: 0,
      };

      if (index === updated.length - 1 && name?.trim()) {
        updated.push(blankInvoiceItem());
      }
      return updated;
    });
    setConfirmedDutySlipRows((prev) => ({ ...prev, [index]: false }));
    setActiveServiceRow(index);
  };

  // ---- Service dropdown UI helpers ----
  const handleServiceInputChange = (rowIndex, value) => {
    handleServiceChange(rowIndex, value);
    if (showAllOnFocusIndex === rowIndex) setShowAllOnFocusIndex(null);
  };

  const selectService = (rowIndex, serviceObj) => {
    handleServiceChange(rowIndex, serviceObj);
    setActiveServiceRow(null);
    setShowAllOnFocusIndex(null);
  };

  const handleServiceFocus = (rowIndex) => {
    setActiveServiceRow(rowIndex);
    setShowAllOnFocusIndex(rowIndex);
  };

  const handleServiceBlur = () => {
    setTimeout(() => {
      setActiveServiceRow(null);
      setShowAllOnFocusIndex(null);
    }, 120);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (index === items.length - 1) {
        setItems((prev) => [...prev, blankInvoiceItem()]);
      }
    }
  };

  // ---- Computed totals ----
  const totalAmount = items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const taxTotal = items.reduce((sum, i) => {
    const amt = parseFloat(i.amount) || 0;
    const rate = parseFloat(i.TaxRate) || 0;
    const taxAmt = parseFloat(i.TaxAmount);
    return sum + (!isNaN(taxAmt) && taxAmt > 0 ? taxAmt : (amt * rate) / 100);
  }, 0);
  const lessAdvanceVal = parseFloat(formData.lessAdvance) || 0;
  const isReverseCharge = formData.reverseCharge === "Yes";
  const grandTotal = totalAmount + (isReverseCharge ? 0 : taxTotal) - lessAdvanceVal;

  const selectedComp = JSON.parse(localStorage.getItem("selectedCompany") || "{}");
  const companyStateCode = String(selectedComp.StateCode || selectedComp.State || "08").trim();
  const partyStateCode = String(formData.stateCode || formData.state || companyStateCode).trim();
  const isInterState = partyStateCode && companyStateCode && (partyStateCode !== companyStateCode);

  let totalCgstAmt = 0;
  let totalSgstAmt = 0;
  let totalIgstAmt = 0;

  if (isInterState) {
    totalIgstAmt = taxTotal;
  } else {
    totalCgstAmt = taxTotal / 2;
    totalSgstAmt = taxTotal / 2;
  }

  // ---- Save Configuration and Continue Invoice Submit ----
  const handleSaveConfigAndContinue = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    const CompID = company?.id || selectedCompanyId;
    if (!CompID) {
      showToast.error("Select company first");
      return;
    }

    const finalIncName = (configIncomeSelect === "__NEW__" || incomeLedgers.length === 0 ? configIncomeNew : configIncomeSelect).trim();
    const finalExpName = (configExpenseSelect === "__NEW__" || expenseLedgers.length === 0 ? configExpenseNew : configExpenseSelect).trim();

    if (!finalIncName) {
      showToast.error("Please select or enter an Income Ledger");
      return;
    }
    if (!finalExpName) {
      showToast.error("Please select or enter an Expense Ledger");
      return;
    }

    setSavingConfigModal(true);
    try {
      // 1. If new income ledger, create it
      if (configIncomeSelect === "__NEW__" || !incomeLedgers.some((l) => l.HEADName === finalIncName)) {
        await axios.post(`${BASE_API_URL}/ledger/create`, {
          CompID,
          HEADName: finalIncName,
          GroupCODE: 13,
          OpenBlType: "CR",
          OpeningBal: 0,
        });
      }

      // 2. If new expense ledger, create it
      if (configExpenseSelect === "__NEW__" || !expenseLedgers.some((l) => l.HEADName === finalExpName)) {
        await axios.post(`${BASE_API_URL}/ledger/create`, {
          CompID,
          HEADName: finalExpName,
          GroupCODE: 19,
          OpenBlType: "DR",
          OpeningBal: 0,
        });
      }

      // 3. Save configuration for company
      await axios.post(`${BASE_API_URL}/configuration/${CompID}`, {
        prefix: companyConfig?.prefix || "",
        suffix: companyConfig?.suffix || "",
        resetInvoiceNo: companyConfig?.resetInvoiceNo ?? true,
        invoiceStartNo: companyConfig?.invoiceStartNo || 1,
        incomeHead: finalIncName,
        expenseHead: finalExpName,
      });

      const updatedCfg = {
        ...(companyConfig || {}),
        incomeHead: finalIncName,
        expenseHead: finalExpName,
      };

      setCompanyConfig(updatedCfg);
      setShowConfigModal(false);
      showToast.success("Default Income & Expense heads saved!");

      // 4. Continue saving invoice directly on this click without delay or requiring 2nd click
      await handleSubmit(null, updatedCfg);
    } catch (err) {
      console.error("Error setting configuration:", err);
      showToast.error(err.response?.data?.message || err.message || "Failed to save accounting heads");
    } finally {
      setSavingConfigModal(false);
    }
  };

  // ---- Submit invoice ----
  const handleSubmit = async (e, configOverride = null) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    if (!formData.partyName) {
      showToast.error("Party Name required!");
      setLoading(false);
      return;
    }
    const company = JSON.parse(localStorage.getItem("selectedCompany"));
    const CompID = company?.id || selectedCompanyId;
    if (!CompID) {
      showToast.error("Select company first!");
      setLoading(false);
      return;
    }

    // Check if Company Configuration has both Income and Expense heads configured
    const activeConfig = configOverride || companyConfig;
    const hasIncome = Boolean(activeConfig?.incomeHead && activeConfig.incomeHead.trim());
    const hasExpense = Boolean(activeConfig?.expenseHead && activeConfig.expenseHead.trim());

    if (!hasIncome || !hasExpense) {
      setLoading(false);
      setShowConfigModal(true);
      return;
    }

    const allLinkedDutySlipIds = items.flatMap((i) => i.linkedDutySlipIds || []);
    const linkedPackageItem = items.find((i) => i.linkedPackageId && i.linkedPackageId !== "custom");

    const parsedNum = parseInt(extractInvoiceNumeric(formData.invoiceNo), 10);
    const invoiceNumericVal = !isNaN(parsedNum) ? parsedNum : null;
    const targetInvoiceNo = formData.invoiceNo || editingInvoiceNo || editParam;

    const payload = {
      CompID,
      InvoiceID: loadedInvoiceId,
      InvoiceNo: targetInvoiceNo,
      InvoiceNoNumeric: invoiceNumericVal,
      InvoiceDate: formData.invoiceDate,
      BillTotal: grandTotal,
      Discount: lessAdvanceVal,
      Advance: lessAdvanceVal,
      ReverseCharges: formData.reverseCharge === "Yes" ? 1 : 0,
      ReverseCharge: formData.reverseCharge === "Yes" ? 1 : 0,
      HeadCode: formData.headCode || formData.partyCode || null,
      PartyCode: formData.partyCode || formData.headCode || null,
      PartyName: formData.partyName,
      Address: formData.address || null,
      PartyAddress: formData.address || null,
      State: formData.state || null,
      StateName: formData.state || null,
      StateCode: formData.stateCode || null,
      GSTIN: formData.gstin || null,
      PartyGSTIN: formData.gstin || null,
      GstNo: formData.gstin || null,
      IncomeHeadCode: null,
      DutySlipNo: parseInt(formData.dutySlipNo) || null,
      DutySlipIds: allLinkedDutySlipIds.map(Number),
      TransportMode: null,
      PackageId: linkedPackageItem ? Number(linkedPackageItem.linkedPackageId) : null,
      VehicleNo: formData.vehicleNo || null,
      VehicleType: formData.vehicleType || null,
      DriverName: formData.driverName || null,
      Guest: formData.guest || null,
      BookedBy: formData.bookedBy || null,
      details: items
        .filter((i) => i.service?.trim() || i.quantity || i.rate || (i.linkedDutySlipIds || []).length > 0)
        .map((i) => {
          const isDuty = isDutySlipService(i.service);
          return {
            Date: isDuty ? (i.date || null) : null,
            OpeningDate: isDuty ? (i.openingDate || i.date || null) : null,
            ClosingDate: isDuty ? (i.closingDate || i.date || null) : null,
            Qty: parseInt(i.quantity) || 1,
            Rate: parseFloat(i.rate) || 0,
            Amount: parseFloat(i.amount) || 0,
            Service: i.service || null,
            Particulars: i.Particulars || null,
            Unit: i.unit || null,
            HSNCODE: i.HSNCODE || null,
            TaxRate: parseFloat(i.TaxRate) || 0,
            TaxAmount: parseFloat(i.TaxAmount) || 0,
            TaxName: i.TaxName || null,
            TaxID: i.TaxID || null,
            linkedDutySlipIds: i.linkedDutySlipIds || [],
          };
        }),
    };

    try {
      if (isModifyMode) {
        const res = await apiWithLog({
          method: "PUT",
          url: `${BASE_API_URL}/invoice/update/${CompID}?invoiceNo=${encodeURIComponent(targetInvoiceNo)}`,
          data: payload,
          module: "Billsinvoice",
          action: "U",
        });
        if (res.status === 200 || res.status === 201 || res.data?.success) {
          showToast.save("Invoice modified & saved successfully! Vouchers posted.");
          navigate("/ViewInvoice");
        } else {
          showToast.error(res.data?.message || "Failed to update invoice!");
        }
      } else {
        const res = await apiWithLog({
          method: "POST",
          url: `${BASE_API_URL}/invoice/create-invoice`,
          data: payload,
          module: "Billsinvoice",
          action: "A",
        });
        if (res.status === 201) {
          showToast.save("Invoice saved successfully! Vouchers posted.");
          // Reset entire form
          const today = new Date().toISOString().split("T")[0];
          let nextInvNo = "";
          try {
            const next = await axios.get(`${BASE_API_URL}/invoice/next-invoice/${CompID}`);
            if (next.data.success) {
              nextInvNo = next.data.nextInvoiceDisplay || formatInvoiceNumber(
                next.data.nextInvoiceNo || extractInvoiceNumeric(next.data.nextInvoiceDisplay) || "1",
                today,
                company?.CompanyCode || next.data.companyCode || "DDT"
              );
            }
          } catch (_) {}
          setFormData({
            invoiceNo: nextInvNo,
            dutySlipNo: "",
            invoiceDate: today,
            vehicleNo: "",
            vehicleType: "",
            driverName: "",
            reverseCharge: "No",
            partyName: "",
            guest: "",
            bookedBy: "",
            address: "",
            state: "",
            stateCode: "",
            gstin: "",
            lessAdvance: "",
          });
          setItems([blankInvoiceItem()]);
          setUninvoicedDutySlips([]);
          setFarePackages([]);
          setManualInvoice(false);
          setConfirmedDutySlipRows({});
        } else {
          showToast.error("Failed to save invoice!");
        }
      }
    } catch (err) {
      console.error(err);
      showToast.error(err.response?.data?.error || err.response?.data?.message || "Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  // ---- Render ----
  return (
    <div className="Payment_div invoice-page" style={{ position: "relative" }}>
      {/* Breadcrumb */}
      <div className="invoice-breadcrumb">
        <span onClick={() => navigate("/home")}>Dashboard</span>
        <i className="fa-solid fa-chevron-right breadcrumb-sep"></i>
        <span onClick={() => navigate("/ViewInvoice")}>Invoices</span>
        <i className="fa-solid fa-chevron-right breadcrumb-sep"></i>
        <strong>{isModifyMode ? "Modify Invoice" : "Create Invoice"}</strong>
      </div>

      {/* Top Action Bar */}
      <div className="invoice-top-bar">
        <div className="invoice-top-title">
          <i className={isModifyMode ? "fa-solid fa-pen-to-square invoice-top-icon" : "fa-solid fa-file-invoice-dollar invoice-top-icon"}></i>
          <h5>{isModifyMode ? "Modify Invoice" : "Create Invoice"}</h5>
        </div>
        <div className="invoice-top-actions">
          <button
            type="button"
            className="btn-view-invoices"
            onClick={() => navigate("/ViewInvoice")}
          >
            <i className="fa-solid fa-list-ul"></i> View Invoices
          </button>
          {isModifyMode && (
            <button
              type="button"
              className="btn-view-invoices"
              onClick={() => {
                navigate("/BillsInvoice");
                window.location.href = "/BillsInvoice";
              }}
              style={{ background: "#f8fafc", color: "#334155" }}
            >
              <i className="fa-solid fa-plus"></i> + New Invoice
            </button>
          )}
          <button
            type="button"
            className="save-btn"
            onClick={handleSubmit}
            disabled={loading || isInvoiceLoading}
          >
            <i className={loading ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-floppy-disk"}></i>
            {loading ? "Saving..." : isModifyMode ? "Update / Modify Invoice" : "Save Invoice"}
          </button>
        </div>
      </div>

      {/* Modify Selector Dropdown when in modify mode */}
      {isModifyMode && (
        <div
          style={{
            background: "#fff",
            borderRadius: "8px",
            padding: "12px 18px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "wrap",
            borderLeft: "4px solid #f59e0b",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i className="fa-solid fa-pen-to-square" style={{ color: "#f59e0b", fontSize: "16px" }}></i>
            <span style={{ fontWeight: 700, fontSize: "13px", color: "#1e293b" }}>
              Select Invoice to Modify:
            </span>
            <select
              value={editingInvoiceNo}
              onChange={(e) => {
                const val = e.target.value;
                setEditingInvoiceNo(val);
                if (val) {
                  navigate(`/BillsInvoice?edit=${encodeURIComponent(val)}`);
                  if (val === editParam) {
                    loadInvoiceForModification(val);
                  }
                }
              }}
              style={{
                height: "36px",
                minWidth: "260px",
                padding: "0 10px",
                borderRadius: "6px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                background: "#fff",
                outline: "none",
                cursor: "pointer",
              }}
            >
              <option value="">-- Choose Invoice to Edit --</option>
              {existingInvoices.map((inv, i) => (
                <option key={i} value={inv.InvoiceNo}>
                  {inv.InvoiceNo} - {inv.PartyName} (₹{Number(inv.BillTotal || 0).toLocaleString("en-IN")})
                </option>
              ))}
            </select>
          </div>
          {isInvoiceLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SkeletonBone width="130px" height="18px" borderRadius="4px" />
              <SkeletonBone width="80px" height="18px" borderRadius="4px" />
            </div>
          )}
        </div>
      )}

      <div className="invoice-card">
        <div className="invoice-card-header">
          <h6>
            <i className="fa-solid fa-building-user" style={{ color: "var(--accent-teal)" }}></i>
            Invoice & Customer Details
          </h6>
        </div>

        <form onSubmit={handleSubmit} className="invoice-form">
          <div className="invoice-fields-container">
            {/* Row 1: Invoice No (170px), Invoice Date (145px), Party Name (292px), State (220px), Address (285px) */}
            <div className="invoice-fields-row">
              <div className={`ifield ifield--inv-no ${isModifyMode ? "with-modify" : ""}`} style={{ width: isModifyMode ? "240px" : "170px", flexShrink: 0 }}>
                <label>Invoice No</label>
                <div style={{ display: "flex", gap: "6px", alignItems: "stretch" }}>
                  <input
                    type="text"
                    name="invoiceNo"
                    maxLength={60}
                    value={formData.invoiceNo}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, invoiceNo: e.target.value }));
                      setManualInvoice(true);
                    }}
                    placeholder="e.g. DDT/8463/2026-2027"
                    style={{ flex: 1 }}
                  />
                  {isModifyMode && (
                    <button
                      type="button"
                      title={
                        canUpdatePrefixSuffix
                          ? `Update prefix/suffix to latest configuration: ${computedPrefixSuffixNo}`
                          : "Prefix & suffix are already updated to current configuration"
                      }
                      style={{
                        whiteSpace: "nowrap",
                        padding: "4px 8px",
                        fontSize: "11px",
                        fontWeight: 600,
                        borderRadius: "4px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        border: canUpdatePrefixSuffix ? "1px solid #0284c7" : "1px solid #cbd5e1",
                        backgroundColor: canUpdatePrefixSuffix ? "#e0f2fe" : "#f1f5f9",
                        color: canUpdatePrefixSuffix ? "#0369a1" : "#94a3b8",
                        cursor: canUpdatePrefixSuffix ? "pointer" : "not-allowed",
                        transition: "all 0.2s ease",
                      }}
                      onClick={handleUpdatePrefixSuffix}
                      disabled={!canUpdatePrefixSuffix}
                    >
                      <i className="fa-solid fa-arrows-rotate"></i>
                      Update
                    </button>
                  )}
                </div>
              </div>

              <div className="ifield ifield--date" style={{ width: "145px", flexShrink: 0 }}>
                <label>Invoice Date</label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                />
              </div>

              <div className="ifield ifield--party" style={{ width: "292px", flexShrink: 0 }}>
                <div className="ifield-label-row">
                  <label>Party Name</label>
                  <div className="party-filter-wrap">
                    <button
                      type="button"
                      className={`party-filter-btn ${partyFilter === "all" ? "active" : ""}`}
                      onClick={() => setPartyFilter("all")}
                      title="Show all parties"
                    >
                      All ({parties.length})
                    </button>
                    <button
                      type="button"
                      className={`party-filter-btn ${partyFilter === "with_duty_slip" ? "active" : ""}`}
                      onClick={() => setPartyFilter("with_duty_slip")}
                      title="Show only parties with pending uninvoiced duty slips"
                    >
                      <i className="fa-solid fa-receipt" style={{ fontSize: "8.5px" }}></i>
                      With Duty Slip ({partiesWithDutySlipsCount})
                    </button>
                  </div>
                </div>
                <Select
                  options={displayedPartyOptions}
                  value={selectedPartyOption}
                  onChange={handlePartyChange}
                  placeholder="-- Search or select party --"
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  noOptionsMessage={() => (partyFilter === "with_duty_slip" ? "No parties with pending duty slips" : "No matching party found")}
                />
              </div>

              <div className="ifield ifield--state" style={{ width: "220px", flexShrink: 0 }}>
                <label>State</label>
                <CreatableSelect
                  options={stateOptions}
                  value={selectedStateOption}
                  onChange={handleStateChange}
                  placeholder="-- Select state --"
                  isClearable
                  isSearchable
                  styles={customSelectStyles}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                  noOptionsMessage={() => "No matching state"}
                />
              </div>

              <div className="ifield ifield--address" style={{ width: "285px", flexShrink: 0 }}>
                <label>Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  maxLength={255}
                  placeholder="Address"
                />
              </div>
            </div>

            {/* Row 2: Rev. Charge (170px), Guest (145px), Vehicle No (150px) + Vehicle Type (130px) = 292px, Driver Name (220px), GSTIN (285px) */}
            <div className="invoice-fields-row">
              <div className="ifield ifield--rev-charge ifield--toggle" style={{ width: isModifyMode ? "240px" : "170px", flexShrink: 0 }}>
                <label>Rev. Charge</label>
                <div style={{ height: "28px", display: "flex", alignItems: "center" }}>
                  <div
                    className={`toggle-switch ${formData.reverseCharge === "Yes" ? "active" : "inactive"}`}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        reverseCharge: prev.reverseCharge === "Yes" ? "No" : "Yes",
                      }))
                    }
                  >
                    <div className="toggle-thumb" />
                    <span className="toggle-label">{formData.reverseCharge}</span>
                  </div>
                </div>
              </div>

              <div className="ifield ifield--guest" style={{ width: "145px", flexShrink: 0 }}>
                <label>Guest Name</label>
                <input
                  type="text"
                  name="guest"
                  value={formData.guest}
                  onChange={handleChange}
                  placeholder="Enter guest name"
                />
              </div>

              <div style={{ display: "flex", gap: "12px", width: "292px", flexShrink: 0 }}>
                <div className="ifield ifield--vehicle-no" style={{ width: "150px", flexShrink: 0 }}>
                  <label>Vehicle Number</label>
                  <CreatableSelect
                    options={vehicleOptions}
                    value={selectedVehicleOption}
                    onChange={handleVehicleChange}
                    placeholder="-- Vehicle --"
                    isClearable
                    isSearchable
                    styles={customSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    noOptionsMessage={() => "No matching vehicle"}
                  />
                </div>

                <div className="ifield ifield--vehicle-type" style={{ width: "130px", flexShrink: 0 }}>
                  <label>Vehicle Type</label>
                  <input
                    type="text"
                    name="vehicleType"
                    value={formData.vehicleType || ""}
                    onChange={handleChange}
                    placeholder="Vehicle Type"
                    style={{ background: formData.vehicleType ? "#f0fff4" : "" }}
                  />
                </div>
              </div>

              <div className="ifield ifield--driver" style={{ width: "220px", flexShrink: 0 }}>
                <label>Driver Name</label>
                <input
                  type="text"
                  name="driverName"
                  value={formData.driverName || ""}
                  onChange={handleChange}
                  placeholder="Driver Name"
                />
              </div>

              <div className="ifield ifield--gstin" style={{ width: "285px", flexShrink: 0 }}>
                <label>GSTIN</label>
                <input
                  type="text"
                  name="gstin"
                  value={formData.gstin || ""}
                  onChange={handleChange}
                  placeholder="GSTIN"
                  maxLength={15}
                />
              </div>
            </div>
          </div>
        </form>
      </div>

          

      {/* Invoice Items Table */}
      <div className="invoice-preview">
        <div className="invoice-card-header">
          <h6>
            <i className="fa-solid fa-receipt" style={{ color: "var(--accent-teal)" }}></i>
            Invoice Line Items
          </h6>
        </div>
        <div className="invoice-preview-container">
          <table className="invoice-table">
            <thead>
              <tr>
                <th style={{ width: "110px" }}>Date</th>
                <th style={{ width: "260px" }}>Services</th>
                <th>Particulars</th>
                <th style={{ width: "90px" }}>Units</th>
                <th style={{ width: "100px" }}>Rate</th>
                <th style={{ width: "100px" }}>Amount</th>
                <th style={{ width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const isAuto = item.isAutoGenerated;

                return (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        {/* Show date: when no service selected, or service is transport */}
                        {(!item.service || isDutySlipService(item.service)) ? (
                          isAuto ? (
                            <span className="cell-input readonly" style={{ display: "inline-block" }}>
                              {formatDateRangeDisplay(item.openingDate, item.closingDate) || toDisplayDate(item.date)}
                            </span>
                          ) : (
                            <input
                              type="date"
                              value={item.date}
                              onChange={(e) => handleItemChange(index, "date", e.target.value)}
                              className="cell-input"
                            />
                          )
                        ) : (
                          <span className="cell-input readonly" style={{ display: "inline-block", color: "#aaa" }}>—</span>
                        )}
                      </td>
                      <td className="service-cell">
                        <div className="service-dropdown-wrapper">
                          <input
                            type="text"
                            value={item.service}
                            onChange={(e) => handleServiceInputChange(index, e.target.value)}
                            onBlur={handleServiceBlur}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="cell-input service-input"
                            placeholder={isAuto ? "" : "Select or type..."}
                            readOnly={isAuto}
                            onFocus={() => !isAuto && handleServiceFocus(index)}
                          />
                          {!isAuto && activeServiceRow === index && (
                            <ul className="service-dropdown-list">
                              <li onMouseDown={() => selectService(index, TRANSPORT_WITH_DUTY_SLIP)}>
                                {TRANSPORT_WITH_DUTY_SLIP}
                              </li>
                              <li onMouseDown={() => selectService(index, TRANSPORT_WITHOUT_DUTY_SLIP)}>
                                {TRANSPORT_WITHOUT_DUTY_SLIP}
                              </li>
                              <li className="dropdown-divider" />
                              {services
                                .filter((s) => {
                                  if (
                                    s.ServiceName === TRANSPORT_WITH_DUTY_SLIP ||
                                    s.ServiceName === TRANSPORT_WITHOUT_DUTY_SLIP
                                  )
                                    return false;
                                  if (showAllOnFocusIndex === index) return true;
                                  const input = (item.service || "").toLowerCase();
                                  return s.ServiceName.toLowerCase().includes(input);
                                })
                                .map((s, i) => (
                                  <li key={i} onMouseDown={() => selectService(index, s)}>
                                    {s.ServiceName}
                                  </li>
                                ))}
                            </ul>
                          )}
                        </div>
                      </td>
                      <td className="particulars-cell">
                        {item.service === TRANSPORT_WITH_DUTY_SLIP ? (
                          confirmedDutySlipRows[index] ? (
                            <div className="particulars-dropdown-wrapper">
                              <button
                                type="button"
                                className="particulars-summary-btn"
                                onClick={() =>
                                  setParticularsDropdownOpen(particularsDropdownOpen === index ? null : index)
                                }
                              >
                                {(() => {
                                  const { slips } = getRowSlipStats(item);
                                  if (!slips.length) {
                                    if (item.Particulars && typeof item.Particulars === "string" && !item.Particulars.startsWith("{") && !item.Particulars.startsWith("[")) {
                                      return <span>{item.Particulars}</span>;
                                    }
                                    return <span>Select duty slips</span>;
                                  }
                                  if (slips.length === 1) {
                                    const first = slips[0];
                                    const pkg = first?.PackageName || "Package";
                                    const openD = formatDateRangeDisplay(
                                      first?.OpeningDate || first?.SlipDate,
                                      first?.ClosingDate || first?.SlipDate
                                    );
                                    const route = getTripDetailsString(first);
                                    return (
                                      <>
                                        <strong>{pkg}</strong>
                                        {openD ? <> &nbsp;|&nbsp; {openD}</> : null}
                                        {route  ? <> &nbsp;|&nbsp; <em>{route}</em></> : null}
                                        &nbsp;(1 slip)
                                      </>
                                    );
                                  }
                                  const slipNumbers = slips.map(s => `#${s.DutySlipID}`).join(", ");
                                  const packages = [...new Set(slips.map(s => s.PackageName || s.packageName).filter(Boolean))].join(", ");
                                  const totalKm = slips.reduce((sum, s) => sum + (s.TotalKM || 0), 0);
                                  return (
                                    <>
                                      <strong>{packages || "Multiple Packages"}</strong>
                                      &nbsp;|&nbsp; <span>{slipNumbers}</span>
                                      &nbsp;|&nbsp; <span>{totalKm} km</span>
                                      &nbsp;({slips.length} slips)
                                    </>
                                  );
                                })()}
                                <span className="dropdown-caret">{particularsDropdownOpen === index ? "▲" : "▼"}</span>
                              </button>


                              {particularsDropdownOpen === index && (() => {
                                const { slips, totalKm, totalTime, totalAmount, uniqueDays } = getRowSlipStats(item);
                                return (
                                  <div
                                    className="particulars-modal-overlay"
                                    onMouseDown={(e) => {
                                      if (e.target === e.currentTarget)
                                        setParticularsDropdownOpen(null);
                                    }}
                                  >
                                    <div className="particulars-dropdown-panel">
                                      {/* ── Modal header ── */}
                                      <div className="particulars-modal-header">
                                        <span className="particulars-modal-title">
                                          Duty Slip Details &nbsp;·&nbsp; {slips.length} slip{slips.length > 1 ? "s" : ""}
                                        </span>
                                        <button
                                          type="button"
                                          className="particulars-modal-close"
                                          onClick={() => setParticularsDropdownOpen(null)}
                                        >×</button>
                                      </div>

                                      {/* ── Summary stats ── */}
                                      <div className="summary-stats">
                                        <span><strong>Total slips:</strong> {slips.length}</span>
                                        <span><strong>Total KM:</strong> {totalKm}</span>
                                        <span><strong>Total time:</strong> {totalTime} hrs</span>
                                        <span><strong>Total days:</strong> {uniqueDays}</span>
                                        <span><strong>Total amount:</strong> ₹{totalAmount.toFixed(2)}</span>
                                      </div>

                                      {/* ── Slip table — professional day-wise breakdown ── */}
                                      <div className="table-scroll" style={{ overflowX: "auto", marginTop: 10 }}>
                                        {slips.map((s) => {
                                          const openDate = toDisplayDate(toInputDate(s.OpeningDate || s.SlipDate));
                                          const closeDate = toDisplayDate(toInputDate(s.ClosingDate || s.SlipDate));
                                          const details = Array.isArray(s.dateWiseDetails) ? s.dateWiseDetails : [];
                                          const fallbackPkg = (s.PackageName || s.packageName || "").trim();
                                          const fmtTime = (v) => {
                                            const mins = parseTimeToMins(v);
                                            const h = Math.floor(mins / 60);
                                            const m = Math.round(mins % 60);
                                            return `${h}h ${m}m`;
                                          };
                                          const fmtAmt = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

                                          // Cumulative trip metrics
                                          let totalIncKM = details.reduce((sum, d) => sum + (resolveDayPackageMetrics(d, s, farePackages).incKM || 0), 0);
                                          let totalIncMins = details.reduce((sum, d) => sum + (resolveDayPackageMetrics(d, s, farePackages).incHrs * 60 || 0), 0);
                                          
                                          const rawModalTopKm = Number(s.TotalKM ?? s.totalKM ?? s.totalKm ?? s.km ?? 0);
                                          const firstModalStart = Number(details[0]?.kmStart || details[0]?.kmStartGarage || details[0]?.kmStartUser || 0);
                                          const lastModalClose = Number(details[details.length - 1]?.kmClose || details[details.length - 1]?.kmCloseGarage || 0);
                                          const modalTripSpan = (lastModalClose > firstModalStart && firstModalStart > 0) ? (lastModalClose - firstModalStart) : 0;
                                          const modalDaysSum = details.reduce((sum, d) => sum + (Number(d.totalKm) || 0), 0);
                                          let totalActualKM = rawModalTopKm > 0 ? rawModalTopKm : (modalTripSpan > 0 ? modalTripSpan : (modalDaysSum > 0 ? modalDaysSum : 0));
                                          let totalActualMins = details.reduce((sum, d) => sum + (parseTimeToMins(d.totalTime) || 0), 0) || parseTimeToMins(s.TotalTime || 0);

                                          const tripExtraKM = details.length > 0 ? Math.max(0, totalActualKM - totalIncKM) : Math.max(0, (Number(s.TotalKM) || 0) - (Number(s.IncludedKM) || 0));
                                          const tripExtraMins = details.length > 0 ? Math.max(0, totalActualMins - totalIncMins) : Math.max(0, (parseTimeToMins(s.TotalTime) || 0) - ((Number(s.IncludedHrs) || 0) * 60));

                                          // Compute day-wise calculation rows
                                          const dayRows = details.map((d, idx) => {
                                            const m = calculateDutySlipDayMetrics(d, s, farePackages);
                                            const dayKm = (details.length === 1 || modalDaysSum > rawModalTopKm) ? (idx === 0 ? totalActualKM : 0) : (Number(d.totalKm) || 0);
                                            const dayMins = parseTimeToMins(d.totalTime) || (details.length === 1 ? parseTimeToMins(s.TotalTime) : 0);
                                            const route = (d.tripDetails || d.TripDetails || d.route || d.Route || s.tripDetails || s.TripDetails || s.Route || s.route || "").trim();

                                            return {
                                              day: idx + 1,
                                              date: d.date ? toDisplayDate(toInputDate(d.date)) : "",
                                              pkgName: m.pkgName || fallbackPkg,
                                              packageType: m.packageType,
                                              route,
                                              km: dayKm,
                                              time: dayMins,
                                              baseFare: m.baseFare,
                                              incKM: m.incKM,
                                              incHrs: m.incHrs,
                                              extraKM: 0,
                                              kmRate: m.kmRate,
                                              kmCharge: 0,
                                              extraMins: 0,
                                              timeRate: m.timeRate,
                                              timeCharge: 0,
                                              dayTotal: m.baseFare
                                            };
                                          });

                                          const mainKmRate = dayRows.find(r => r.kmRate > 0)?.kmRate || Number(s.FuelRateKM || 0);
                                          const mainTimeRate = dayRows.find(r => r.timeRate > 0)?.timeRate || Number(s.TimeRatePM || 0);
                                          const cumulativeKmCharge = tripExtraKM * mainKmRate;
                                          const cumulativeTimeCharge = (tripExtraMins / 60) * mainTimeRate;

                                          const parking = Number(s.Parking || s.parking) || 0;
                                          const driverAllowance = Number(s.DriverAllowance || s.driverAllowance) || 0;
                                          const tollTax = Number(s.TollTax || s.tollTax) || 0;
                                          const nightCharges = Number(s.NightCharges || s.nightCharges) || 0;
                                          const addOns = parking + driverAllowance + tollTax + nightCharges;
                                          const baseFareSubtotal = dayRows.length > 0 ? dayRows.reduce((sum, d) => sum + d.dayTotal, 0) : (Number(s.BaseFare) || 0);
                                          const fareSubtotal = baseFareSubtotal + cumulativeKmCharge + cumulativeTimeCharge;
                                          const grandTotal = fareSubtotal + addOns;

                                          const thSty = { padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#1a3c34", background: "#f0faf7", borderBottom: "2px solid #0d9488", textAlign: "left", whiteSpace: "nowrap" };
                                          const tdSty = { padding: "6px 10px", fontSize: 11, borderBottom: "1px solid #e5e7eb", verticalAlign: "top" };
                                          const dayBadgeSty = { display: "inline-block", background: "#eff6ff", color: "#1d4ed8", borderRadius: 12, padding: "2px 10px", fontSize: 10, fontWeight: 600, marginRight: 4 };
                                          const isSlipOutstation = checkIsOutstation(s, farePackages);
                                          const hasZeroRates = isSlipOutstation
                                            ? (Number(s.FuelRateKM || 0) === 0 && Number(s.LubRateKM || 0) === 0 && mainKmRate === 0)
                                            : (Number(s.BaseFare || 0) === 0 && Number(s.FuelRateKM || 0) === 0 && Number(s.TimeRatePM || 0) === 0);
                                          const isEditingThisSlip = editingSlipId === s.DutySlipID;

                                          return (
                                            <div key={s.DutySlipID} style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                                              {/* Slip header bar */}
                                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc", padding: "8px 14px", borderBottom: "1px solid #e2e8f0" }}>
                                                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                                  <span style={{ fontWeight: 700, color: "#0d9488", fontSize: 13 }}>Slip #{s.DutySlipID}</span>
                                                  {s.isPackageUpgraded && (
                                                    <span
                                                      style={{
                                                        background: "#ecfdf5",
                                                        color: "#047857",
                                                        border: "1px solid #a7f3d0",
                                                        fontSize: 10,
                                                        padding: "2px 6px",
                                                        borderRadius: 4,
                                                        fontWeight: 600,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 3,
                                                      }}
                                                      title={`Auto-upgraded from ${s.originalPackageName || "base package"} because trip reached ${s.IncludedKM} KM threshold`}
                                                    >
                                                      ⚡ Upgraded to {s.PackageName}
                                                    </span>
                                                  )}
                                                  <span style={{ fontSize: 11, color: "#64748b" }}>{openDate} → {closeDate}</span>
                                                  <span style={{ fontSize: 11, color: "#64748b" }}>{s.VehicleType || "—"}</span>
                                                  {s.VehicleNo && <span style={{ fontSize: 10, color: "#94a3b8" }}>{s.VehicleNo}</span>}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                  <button
                                                    type="button"
                                                    style={{
                                                      background: isEditingThisSlip ? "#e2e8f0" : (hasZeroRates ? "#fef3c7" : "#eff6ff"),
                                                      color: isEditingThisSlip ? "#475569" : (hasZeroRates ? "#b45309" : "#1d4ed8"),
                                                      border: `1px solid ${hasZeroRates ? "#fde68a" : "#bfdbfe"}`,
                                                      borderRadius: 4,
                                                      padding: "3px 8px",
                                                      fontSize: 11,
                                                      fontWeight: 600,
                                                      cursor: "pointer",
                                                    }}
                                                    onClick={() => isEditingThisSlip ? setEditingSlipId(null) : startEditingSlip(s)}
                                                  >
                                                    {isEditingThisSlip ? "✖ Close Edit" : (hasZeroRates ? "⚠️ Set Rates" : "✏️ Edit Rates")}
                                                  </button>
                                                  <div style={{ fontWeight: 700, color: "#0f766e", fontSize: 14 }}>{fmtAmt(grandTotal)}</div>
                                                </div>
                                              </div>

                                              {/* 0-rate notice if no rate found for car/company */}
                                              {hasZeroRates && !isEditingThisSlip && (
                                                <div style={{ background: "#fffbeb", borderBottom: "1px solid #fef08a", color: "#854d0e", padding: "6px 14px", fontSize: 11, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                  <span>⚠️ <strong>No rates configured</strong> for {s.VehicleType || "this car"} in this company. Base & extra rates are ₹0.</span>
                                                  <button
                                                    type="button"
                                                    style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: 4, padding: "2px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600 }}
                                                    onClick={() => startEditingSlip(s)}
                                                  >
                                                    Enter Rates
                                                  </button>
                                                </div>
                                              )}

                                              {/* Inline edit form */}
                                              {isEditingThisSlip && (
                                                <div style={{ background: "#f8fafc", padding: "12px 14px", borderBottom: "1px solid #cbd5e1" }}>
                                                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 20, marginBottom: 10, flexWrap: "wrap" }}>
                                                    <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 600, color: slipEditForm.fareType === "receivable" ? "#0f766e" : "#475569" }}>
                                                      <input
                                                        type="radio"
                                                        name={`fareType_${s.DutySlipID}`}
                                                        value="receivable"
                                                        checked={slipEditForm.fareType === "receivable"}
                                                        onChange={() => {
                                                          const rec = s.receivableRates || s;
                                                          const isOut = checkIsOutstation(rec, farePackages);
                                                          setSlipEditForm((prev) => ({
                                                            ...prev,
                                                            fareType: "receivable",
                                                            PackageName: rec.PackageName || rec.packageName || prev.PackageName,
                                                            isOutstation: isOut,
                                                            BaseFare: isOut ? 0 : (rec.BaseFare !== undefined ? rec.BaseFare : prev.BaseFare),
                                                            IncludedKM: rec.IncludedKM !== undefined ? rec.IncludedKM : prev.IncludedKM,
                                                            IncludedHrs: isOut ? 0 : (rec.IncludedHrs !== undefined ? rec.IncludedHrs : prev.IncludedHrs),
                                                            FuelRateKM: rec.FuelRateKM !== undefined ? rec.FuelRateKM : prev.FuelRateKM,
                                                            LubRateKM: rec.LubRateKM !== undefined ? rec.LubRateKM : prev.LubRateKM,
                                                            TimeRatePM: isOut ? 0 : (rec.TimeRatePM !== undefined ? rec.TimeRatePM : prev.TimeRatePM),
                                                          }));
                                                        }}
                                                      />
                                                      Receivable Fare ({formData.partyName || s.PartyName || "Party Contract"})
                                                    </label>
                                                    <label style={{ fontSize: 11, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 600, color: slipEditForm.fareType === "payable" ? "#0f766e" : "#475569" }}>
                                                      <input
                                                        type="radio"
                                                        name={`fareType_${s.DutySlipID}`}
                                                        value="payable"
                                                        checked={slipEditForm.fareType === "payable"}
                                                        onChange={() => {
                                                          const pay = s.payableRates || s;
                                                          const isOut = checkIsOutstation(pay, farePackages);
                                                          setSlipEditForm((prev) => ({
                                                            ...prev,
                                                            fareType: "payable",
                                                            PackageName: pay.PackageName || pay.packageName || prev.PackageName,
                                                            isOutstation: isOut,
                                                            BaseFare: isOut ? 0 : (pay.BaseFare !== undefined ? pay.BaseFare : prev.BaseFare),
                                                            IncludedKM: pay.IncludedKM !== undefined ? pay.IncludedKM : prev.IncludedKM,
                                                            IncludedHrs: isOut ? 0 : (pay.IncludedHrs !== undefined ? pay.IncludedHrs : prev.IncludedHrs),
                                                            FuelRateKM: pay.FuelRateKM !== undefined ? pay.FuelRateKM : prev.FuelRateKM,
                                                            LubRateKM: pay.LubRateKM !== undefined ? pay.LubRateKM : prev.LubRateKM,
                                                            TimeRatePM: isOut ? 0 : (pay.TimeRatePM !== undefined ? pay.TimeRatePM : prev.TimeRatePM),
                                                          }));
                                                        }}
                                                      />
                                                      Payable Fare (Company General)
                                                    </label>
                                                  </div>

                                                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
                                                    <div>
                                                      <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Package Name (Default)</label>
                                                      <input
                                                        type="text"
                                                        value={slipEditForm.PackageName || ""}
                                                        readOnly
                                                        style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1", background: "#f1f5f9", cursor: "not-allowed", color: "#334155", fontWeight: 600 }}
                                                        title="Package name is fixed from duty slip"
                                                      />
                                                    </div>
                                                    {!slipEditForm.isOutstation && (
                                                      <div>
                                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Base Fare (₹)</label>
                                                        <input
                                                          type="number"
                                                          value={slipEditForm.BaseFare ?? ""}
                                                          onChange={(e) => setSlipEditForm((prev) => ({ ...prev, BaseFare: e.target.value }))}
                                                          style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                        />
                                                      </div>
                                                    )}
                                                    <div>
                                                      <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Included KM</label>
                                                      <input
                                                        type="number"
                                                        value={slipEditForm.IncludedKM ?? ""}
                                                        onChange={(e) => setSlipEditForm((prev) => ({ ...prev, IncludedKM: e.target.value }))}
                                                        style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                      />
                                                    </div>
                                                    {!slipEditForm.isOutstation && (
                                                      <div>
                                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Included Hrs</label>
                                                        <input
                                                          type="number"
                                                          value={slipEditForm.IncludedHrs ?? ""}
                                                          onChange={(e) => setSlipEditForm((prev) => ({ ...prev, IncludedHrs: e.target.value }))}
                                                          style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                        />
                                                      </div>
                                                    )}
                                                    <div>
                                                      <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Fuel Rate (₹/km)</label>
                                                      <input
                                                        type="number"
                                                        value={slipEditForm.FuelRateKM ?? ""}
                                                        onChange={(e) => setSlipEditForm((prev) => ({ ...prev, FuelRateKM: e.target.value }))}
                                                        style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                      />
                                                    </div>
                                                    <div>
                                                      <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Lub Rate (₹/km)</label>
                                                      <input
                                                        type="number"
                                                        value={slipEditForm.LubRateKM ?? ""}
                                                        onChange={(e) => setSlipEditForm((prev) => ({ ...prev, LubRateKM: e.target.value }))}
                                                        style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                      />
                                                    </div>
                                                    {!slipEditForm.isOutstation && (
                                                      <div>
                                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#475569" }}>Extra Hour Rate (₹/hr)</label>
                                                        <input
                                                          type="number"
                                                          value={slipEditForm.TimeRatePM ?? ""}
                                                          onChange={(e) => setSlipEditForm((prev) => ({ ...prev, TimeRatePM: e.target.value }))}
                                                          style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #cbd5e1" }}
                                                        />
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                                    <button
                                                      type="button"
                                                      style={{ background: "#0d9488", color: "#fff", border: "none", borderRadius: 4, padding: "5px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
                                                      onClick={() => handleSaveSlipRates(s.DutySlipID, index)}
                                                    >
                                                      ✓ Apply & Save Rates
                                                    </button>
                                                    <button
                                                      type="button"
                                                      style={{ background: "#fff", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: 4, padding: "5px 14px", fontSize: 11, cursor: "pointer" }}
                                                      onClick={() => setEditingSlipId(null)}
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                </div>
                                              )}

                                              {dayRows.length > 0 ? (
                                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                  <thead>
                                                    <tr>
                                                      <th style={thSty}>Day</th>
                                                      <th style={thSty}>Date</th>
                                                      <th style={thSty}>Package</th>
                                                      <th style={thSty}>Route</th>
                                                      <th style={{ ...thSty, textAlign: "right" }}>KM</th>
                                                      <th style={{ ...thSty, textAlign: "right" }}>Time</th>
                                                      <th style={{ ...thSty, textAlign: "right" }}>Base</th>
                                                      <th style={{ ...thSty, textAlign: "right" }}>Extra KM</th>
                                                      <th style={{ ...thSty, textAlign: "right" }}>Extra Time</th>
                                                      <th style={{ ...thSty, textAlign: "right" }}>Day Total</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {dayRows.map((d) => {
                                                         const isOutstation = checkIsOutstation(d, farePackages) || checkIsOutstation(s, farePackages);
                                                         const fRate = d.kmRate > 0 ? d.kmRate : (mainKmRate || 0);
                                                         const baseDisplay = (isOutstation && d.incKM > 0 && fRate > 0)
                                                           ? `${d.incKM} km × ₹${fRate.toFixed(2)} = ${fmtAmt(d.baseFare)}`
                                                           : fmtAmt(d.baseFare);

                                                         return (
                                                           <tr key={d.day} style={{ background: d.day % 2 === 0 ? "#fafafa" : "#fff" }}>
                                                             <td style={tdSty}><span style={dayBadgeSty}>Day {d.day}</span></td>
                                                             <td style={tdSty}>{d.date}</td>
                                                             <td style={{ ...tdSty, fontWeight: 600, color: "#1a3c34", maxWidth: 180 }}>{d.pkgName || "—"}</td>
                                                             <td style={{ ...tdSty, color: "#475569", maxWidth: 160 }}>{d.route || "—"}</td>
                                                             <td style={{ ...tdSty, textAlign: "right", fontWeight: 600 }}>{d.km}</td>
                                                             <td style={{ ...tdSty, textAlign: "right" }}>{fmtTime(d.time / 60)}</td>
                                                             <td style={{ ...tdSty, textAlign: "right", fontSize: isOutstation ? 10 : 11 }}>{baseDisplay}</td>
                                                             <td style={{ ...tdSty, textAlign: "right", color: d.extraKM > 0 ? "#dc2626" : "#94a3b8" }}>
                                                               {d.extraKM > 0 ? `${d.extraKM} km × ₹${d.kmRate || fRate} = ${fmtAmt(d.kmCharge)}` : "—"}
                                                             </td>
                                                             <td style={{ ...tdSty, textAlign: "right", color: d.extraMins > 0 ? "#dc2626" : "#94a3b8" }}>
                                                               {d.extraMins > 0 ? `${Number((d.extraMins / 60).toFixed(2))}h × ₹${d.timeRate}/h = ${fmtAmt(d.timeCharge)}` : "—"}
                                                             </td>
                                                             <td style={{ ...tdSty, textAlign: "right", fontWeight: 700, color: "#0f766e" }}>{fmtAmt(d.dayTotal)}</td>
                                                           </tr>
                                                         );
                                                       })}
                                                     {/* Cumulative extra row if trip exceeded total allowance */}
                                                     {(tripExtraKM > 0 || tripExtraMins > 0) && (
                                                       <tr style={{ background: "#fff5f5" }}>
                                                         <td colSpan={4} style={{ ...tdSty, color: "#dc2626", fontWeight: 600, textAlign: "right" }}>
                                                           Cumulative Trip Extra (Total {totalActualKM} KM vs {totalIncKM} KM Allowed)
                                                         </td>
                                                         <td style={{ ...tdSty, textAlign: "right", color: "#dc2626" }}>{totalActualKM}</td>
                                                         <td style={{ ...tdSty, textAlign: "right", color: "#dc2626" }}>{fmtTime(totalActualMins / 60)}</td>
                                                         <td style={tdSty}></td>
                                                         <td style={{ ...tdSty, textAlign: "right", color: "#dc2626" }}>
                                                           {tripExtraKM > 0 ? `${tripExtraKM} km × ₹${mainKmRate} = ${fmtAmt(cumulativeKmCharge)}` : "—"}
                                                         </td>
                                                         <td style={{ ...tdSty, textAlign: "right", color: "#dc2626" }}>
                                                           {tripExtraMins > 0 ? `${Number((tripExtraMins / 60).toFixed(2))}h × ₹${mainTimeRate}/h = ${fmtAmt(cumulativeTimeCharge)}` : "—"}
                                                         </td>
                                                         <td style={{ ...tdSty, textAlign: "right", fontWeight: 700, color: "#dc2626" }}>
                                                           {fmtAmt(cumulativeKmCharge + cumulativeTimeCharge)}
                                                         </td>
                                                       </tr>
                                                     )}
                                                    {/* Subtotal row */}
                                                    <tr style={{ background: "#f0fdf4", borderTop: "2px solid #0d9488" }}>
                                                      <td colSpan={4} style={{ ...tdSty, fontWeight: 700, color: "#0f766e", textAlign: "right" }}>Fare Subtotal</td>
                                                      <td style={{ ...tdSty, textAlign: "right", fontWeight: 700 }}>{dayRows.reduce((a, d) => a + d.km, 0)}</td>
                                                      <td style={{ ...tdSty, textAlign: "right", fontWeight: 700 }}>{fmtTime(dayRows.reduce((a, d) => a + d.time, 0) / 60)}</td>
                                                      <td colSpan={3} style={tdSty}></td>
                                                      <td style={{ ...tdSty, textAlign: "right", fontWeight: 700, color: "#0f766e", fontSize: 12 }}>{fmtAmt(fareSubtotal)}</td>
                                                    </tr>
                                                  </tbody>
                                                </table>
                                              ) : (
                                                /* Single-day fallback — compact row */
                                                <div style={{ padding: "8px 14px", fontSize: 11, color: "#475569" }}>
                                                  <span style={{ fontWeight: 600 }}>{fallbackPkg || "—"}</span>
                                                  <span style={{ margin: "0 8px", color: "#cbd5e1" }}>|</span>
                                                  {s.TotalKM} km, {fmtTime(s.TotalTime)}
                                                  <span style={{ margin: "0 8px", color: "#cbd5e1" }}>|</span>
                                                  {fmtAmt(grandTotal)}
                                                </div>
                                              )}

                                              {/* Add-ons footer */}
                                              {addOns > 0 && (
                                                <div style={{ display: "flex", gap: 16, padding: "6px 14px", background: "#fffbeb", borderTop: "1px solid #fde68a", fontSize: 10, color: "#92400e", flexWrap: "wrap", alignItems: "center" }}>
                                                  {parking > 0 && <span>Parking: {fmtAmt(parking)}</span>}
                                                  {driverAllowance > 0 && <span>DA: {fmtAmt(driverAllowance)}</span>}
                                                  {tollTax > 0 && <span>Toll: {fmtAmt(tollTax)}</span>}
                                                  {nightCharges > 0 && <span>Night: {fmtAmt(nightCharges)}</span>}
                                                  <span style={{ marginLeft: "auto", fontWeight: 700, color: "#78350f" }}>Add-ons Total: {fmtAmt(addOns)}</span>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* ── Edit selection ── */}
                                      <button
                                        type="button"
                                        className="edit-selection-btn"
                                        style={{ marginTop: 14 }}
                                        onClick={() => {
                                          setConfirmedDutySlipRows((prev) => ({ ...prev, [index]: false }));
                                          setParticularsDropdownOpen(null);
                                        }}
                                      >
                                        Edit selection
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="row-duty-slip-list">
                              {getAvailableDutySlipsForRow(index).length === 0 ? (
                                <div className="empty-hint">No available duty slips</div>
                              ) : (
                                getAvailableDutySlipsForRow(index).map((slip) => (
                                  <label key={slip.DutySlipID} className="duty-slip-item">
                                    <input
                                      type="checkbox"
                                      checked={(item.linkedDutySlipIds || []).includes(slip.DutySlipID)}
                                      onChange={() => toggleDutySlipForRow(index, slip.DutySlipID)}
                                    />
                                    <span className="duty-slip-text">
                                      #{slip.DutySlipID} · {toInputDate(slip.SlipDate)} ·{" "}
                                      {slip.PackageName || "No package"}
                                      {slip.isPackageUpgraded ? " (⚡Upgraded)" : ""} · ₹{Number(calculateSlipTotal(slip)).toFixed(2)}
                                      {slip.VehicleNo ? ` · ${slip.VehicleNo}` : ""}
                                    </span>
                                  </label>
                                ))
                              )}
                              <button
                                type="button"
                                className="confirm-slips-btn"
                                disabled={(item.linkedDutySlipIds || []).length === 0}
                                onClick={() => {
                                  setConfirmedDutySlipRows((prev) => ({ ...prev, [index]: true }));
                                  autoFillFromDutySlips(item.linkedDutySlipIds || []);
                                }}
                              >
                                OK
                              </button>
                            </div>
                          )
                        ) : item.service === TRANSPORT_WITHOUT_DUTY_SLIP ? (
                          <>
                            <select
                              value={item.linkedPackageId || ""}
                              onChange={(e) => selectPackageForRow(index, e.target.value)}
                              className="cell-input"
                            >
                              <option value="">Select package</option>
                              <option value="custom">Custom</option>
                              {farePackages.map((pkg) => (
                                <option key={pkg.ID} value={pkg.ID}>
                                  {pkg.PackageName} | ₹{Number(pkg.BaseFare || 0).toFixed(2)}
                                </option>
                              ))}
                            </select>
                            {item.linkedPackageId === "custom" && (
                              <input
                                type="text"
                                value={item.Particulars}
                                onChange={(e) => handleItemChange(index, "Particulars", e.target.value)}
                                className="cell-input"
                                placeholder="Enter custom particulars"
                                style={{ marginTop: 4 }}
                              />
                            )}
                          </>
                        ) : (
                          <input
                            type="text"
                            value={item.Particulars}
                            onChange={(e) => handleItemChange(index, "Particulars", e.target.value)}
                            className="cell-input"
                            readOnly={isAuto}
                          />
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={item.unit || ""}
                          onChange={(e) => handleItemChange(index, "unit", e.target.value)}
                          className="cell-input"
                          placeholder="e.g. Trip, Day, Lump Sum"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                          className="cell-input"
                          readOnly={isAuto}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={
                            item.amount !== undefined
                              ? Number(item.amount).toFixed(2)
                              : "0.00"
                          }
                          readOnly
                          className="cell-input readonly"
                        />
                      </td>
                      <td className="action-cell">
                        <button
                          type="button"
                          onClick={() => {
                            if (items.length > 1) {
                              setItems((prev) => prev.filter((_, i) => i !== index));
                            }
                          }}
                          title="Delete Row"
                        >
                          <i className="fa-solid fa-trash-can"></i>
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>

            <tfoot>
              <tr className="total-taxable-row">
                <td colSpan="5" style={{ textAlign: "right", fontWeight: "600" }}>
                  Total Taxable Amount:
                </td>
                <td colSpan="2">
                  <input
                    type="text"
                    value={totalAmount.toFixed(2)}
                    readOnly
                    className="cell-input readonly"
                    style={{ textAlign: "right", fontWeight: "600" }}
                  />
                </td>
              </tr>

              <tr>
                <td colSpan="5" style={{ textAlign: "right", fontWeight: "600" }}>
                  Total GST:
                </td>
                <td colSpan="2">
                  <input
                    type="text"
                    value={Number(taxTotal).toFixed(2)}
                    readOnly
                    className="cell-input readonly"
                    style={{ textAlign: "right" }}
                  />
                </td>
              </tr>

              {formData.reverseCharge === "Yes" && (
                <tr>
                  <td colSpan="5" style={{ textAlign: "right", fontWeight: "600" }}>
                    GST Payable on Reverse Charge:
                  </td>
                  <td colSpan="2">
                    <input
                      type="text"
                      value={Number(taxTotal).toFixed(2)}
                      readOnly
                      className="cell-input readonly"
                      style={{ textAlign: "right" }}
                    />
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan="5" style={{ textAlign: "right", fontWeight: "600" }}>
                  Less Advance:
                </td>
                <td colSpan="2">
                  <input
                    type="number"
                    name="lessAdvance"
                    value={formData.lessAdvance}
                    onChange={handleChange}
                    placeholder="0.00"
                    className="cell-input"
                    style={{ textAlign: "right" }}
                  />
                </td>
              </tr>

              <tr className="grand-total-row">
                <td colSpan="5" style={{ textAlign: "right", fontWeight: "700" }}>
                  Grand Total:
                </td>
                <td colSpan="2">
                  <input
                    type="text"
                    value={Number(grandTotal).toFixed(2)}
                    readOnly
                    className="cell-input readonly"
                    style={{ textAlign: "right", fontWeight: "800" }}
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="invoice-bottom-actions">
          <button
            type="button"
            className="add-row-btn"
            onClick={() => setItems((prev) => [...prev, blankInvoiceItem()])}
          >
            <i className="fa-solid fa-plus"></i> Add Row
          </button>
        </div>
      </div>

      {/* Configuration Required Modal when saving invoice */}
      {showConfigModal && (
        <div
          className="delete-modal-overlay"
          onClick={() => setShowConfigModal(false)}
        >
          <div
            className="delete-modal"
            style={{ maxWidth: "480px", textAlign: "left" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", display: "flex", alignItems: "center", gap: "8px" }}>
                <i className="fa-solid fa-sliders" style={{ color: "var(--accent-teal, #0d9488)" }}></i>
                Setup Accounting Default Heads
              </h3>
              <button
                type="button"
                onClick={() => setShowConfigModal(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: "16px",
                  cursor: "pointer",
                  color: "#94a3b8",
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ margin: "0 0 16px", fontSize: "12px", color: "#64748b", lineHeight: "1.5" }}>
              Automatic voucher creation requires a <strong>Default Income Head</strong> and <strong>Default Expense Head</strong>. Please select or enter new ledgers for this company to continue:
            </p>

            <form onSubmit={handleSaveConfigAndContinue}>
              {/* Income Head Field */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "5px" }}>
                  Default Income Head *
                </label>
                <select
                  value={configIncomeSelect}
                  onChange={(e) => setConfigIncomeSelect(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    outline: "none",
                    marginBottom: (configIncomeSelect === "__NEW__" || incomeLedgers.length === 0) ? "8px" : "0",
                  }}
                >
                  <option value="">-- Select Income Ledger --</option>
                  {incomeLedgers.map((l) => (
                    <option key={`inc-${l.HEAD_CODE}`} value={l.HEADName}>
                      {l.HEADName}
                    </option>
                  ))}
                  <option value="__NEW__">+ Create New Income Ledger</option>
                </select>

                {(configIncomeSelect === "__NEW__" || incomeLedgers.length === 0) && (
                  <input
                    type="text"
                    required
                    placeholder="Enter New Income Ledger Name (e.g. Taxi Hire Charges)"
                    value={configIncomeNew}
                    onChange={(e) => setConfigIncomeNew(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "5px",
                      border: "1px solid #0d9488",
                      fontSize: "13px",
                      outline: "none",
                      background: "#f0fdfa",
                    }}
                  />
                )}
              </div>

              {/* Expense Head Field */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "5px" }}>
                  Default Expense Head *
                </label>
                <select
                  value={configExpenseSelect}
                  onChange={(e) => setConfigExpenseSelect(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "5px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    outline: "none",
                    marginBottom: (configExpenseSelect === "__NEW__" || expenseLedgers.length === 0) ? "8px" : "0",
                  }}
                >
                  <option value="">-- Select Expense Ledger --</option>
                  {expenseLedgers.map((l) => (
                    <option key={`exp-${l.HEAD_CODE}`} value={l.HEADName}>
                      {l.HEADName}
                    </option>
                  ))}
                  <option value="__NEW__">+ Create New Expense Ledger</option>
                </select>

                {(configExpenseSelect === "__NEW__" || expenseLedgers.length === 0) && (
                  <input
                    type="text"
                    required
                    placeholder="Enter New Expense Ledger Name (e.g. Taxi Hire Expenses)"
                    value={configExpenseNew}
                    onChange={(e) => setConfigExpenseNew(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "5px",
                      border: "1px solid #0d9488",
                      fontSize: "13px",
                      outline: "none",
                      background: "#f0fdfa",
                    }}
                  />
                )}
              </div>

              <div className="modal-actions" style={{ justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => setShowConfigModal(false)}
                  disabled={savingConfigModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-confirm-btn"
                  style={{ background: "var(--accent-teal, #0d9488)" }}
                  disabled={savingConfigModal}
                >
                  {savingConfigModal ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i> Save & Continue
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillsInvoice;